import React, { useEffect, useState, useCallback, useMemo } from "react";
import {
  Box, Typography, Card, CardContent, Stack, Grid, Avatar, Paper,
  Chip, Divider, IconButton, Tooltip, Fade, LinearProgress, Button,
} from "@mui/material";
import PeopleIcon from "@mui/icons-material/People";
import EmailIcon from "@mui/icons-material/Email";
import EventIcon from "@mui/icons-material/Event";
import WarningIcon from "@mui/icons-material/Warning";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import PhoneIcon from "@mui/icons-material/Phone";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import LocalFireDepartmentIcon from "@mui/icons-material/LocalFireDepartment";
import PersonOffIcon from "@mui/icons-material/PersonOff";
import NotificationsActiveIcon from "@mui/icons-material/NotificationsActive";
import RequestQuoteOutlinedIcon from "@mui/icons-material/RequestQuoteOutlined";
import EventAvailableIcon from "@mui/icons-material/EventAvailable";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import RefreshIcon from "@mui/icons-material/Refresh";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import AssignmentIndOutlinedIcon from "@mui/icons-material/AssignmentIndOutlined";
import CallIcon from "@mui/icons-material/Call";
import MailOutlineIcon from "@mui/icons-material/MailOutline";
import SmsOutlinedIcon from "@mui/icons-material/SmsOutlined";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import HourglassEmptyIcon from "@mui/icons-material/HourglassEmpty";
import HandshakeIcon from "@mui/icons-material/Handshake";
import EditNoteIcon from "@mui/icons-material/EditNote";
import SwapHorizIcon from "@mui/icons-material/SwapHoriz";
import PersonAddAltIcon from "@mui/icons-material/PersonAddAlt";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import HistoryIcon from "@mui/icons-material/History";
import FiberManualRecordIcon from "@mui/icons-material/FiberManualRecord";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import DoneAllIcon from "@mui/icons-material/DoneAll";
import GroupOutlinedIcon from "@mui/icons-material/GroupOutlined";
import CalendarMonthOutlinedIcon from "@mui/icons-material/CalendarMonthOutlined";
import { useNavigate } from "react-router-dom";
import { Loader } from "../../Components/Loader";
import { API_URL } from "../../config";
import { trackedFetch } from "../../Components/Loader/GlobalLoader";
import UserAvatar from "../../Components/UserAvatar";
import { useLeadBus } from "../../hooks/useLeadBus";
import { usePermission } from "../../hooks/usePermission";
import BookingDensity from "./BookingDensity";
import RevenueTrajectory from "./charts/RevenueTrajectory";
import PipelineDonut, { type DonutSegment } from "./charts/PipelineDonut";
import PipelineBars, { type PipelineStage } from "./charts/PipelineBars";
import ConversionGauge from "./charts/ConversionGauge";
import FollowUpDetailModal from "./FollowUpDetailModal";

/* ──────────────────────────────────────────────────────────────────────
   Live Command Center — premium editorial design tokens.

   The dashboard runs on a tight dual palette: near-black CHARCOAL for
   the hero canvas and a warm IVORY field for the working surface,
   accented by a single signature champagne gold. No generic SaaS blues.

   Typography pairing: Hanken Grotesk for the UI / labels, Fraunces
   (high-contrast modern serif) for the page title, section heads, and
   the oversized KPI numerals. JetBrains Mono for the live date /
   monospaced tags. The serif + champagne combination is the single
   strongest signal that this is a hospitality product, not a CRM.

   Borders never exceed 1px and 0.06 opacity. Shadows are always layered
   (two soft drops, never one hard cast). Card radius is 12px; pills are
   999px; the 8px grid is enforced everywhere.
   ──────────────────────────────────────────────────────────────────── */
const CHARCOAL    = "#0A0E1A";  // primary dark surface
const GRAPHITE    = "#1B2236";  // gradient midpoint
const INK         = "#0F172A";  // body text on light surfaces
const IVORY       = "#FAF7F2";  // warm cream working canvas
const IVORY_DEEP  = "#F3EEE5";  // alternate panel tint
const CHAMPAGNE   = "#C9A961";  // signature gold accent
const CHAMPAGNE_DEEP = "#A8893E"; // emphasized gold
const CHAMPAGNE_PALE = "#F5EFE0"; // tinted ground for pills + warm panels
const WINE        = "#7F2D3B";  // burgundy — urgent / past-due
const WINE_PALE   = "#F8E8EB";  // wine pill ground
const SAGE        = "#5C7657";  // success / confirmed
const SAGE_PALE   = "#E8EFE5";  // sage pill ground
const BRONZE      = "#6B5A33";  // deep neutral metallic (graphite-tinted)
const HAIRLINE    = "rgba(10,14,26,0.06)";
const HAIRLINE_LIGHT = "rgba(10,14,26,0.04)";

const FONT_SERIF  = `'Fraunces', 'GT Sectra', 'Tiempos Headline', Georgia, serif`;
const FONT_MONO   = `'JetBrains Mono', 'SF Mono', Menlo, Consolas, monospace`;

/* Backwards-compat aliases — the file historically used NAVY/GOLD names
   in lots of inline `sx` blocks. We re-bind them to the new tokens so
   we don't have to touch every reference. NAVY now reads "ink", GOLD
   reads "champagne". */
const NAVY        = INK;
const NAVY_LIGHT  = GRAPHITE;
const GOLD        = CHAMPAGNE;

/* Card surface tokens — single soft layered shadow + the lightest
   possible hairline border, matching the brief's premium spec. */
const SURFACE = {
  card:       "#ffffff",
  cardBorder: `1px solid ${HAIRLINE}`,
  cardShadow: "0 1px 2px rgba(10,14,26,0.04), 0 4px 12px rgba(10,14,26,0.04)",
  cardRadius: "12px",
  divider:    `1px solid ${HAIRLINE_LIGHT}`,
} as const;

/* ── SectionDivider — editorial hairline with serif eyebrow label ────
   1px champagne-gradient line (transparent → gold → transparent) with
   a small serif italic label centered above it. Inserts between major
   page zones to add magazine-style breathing room. */
function SectionDivider({ label }: { label: string }) {
  return (
    <Box sx={{ position: "relative", textAlign: "center", my: 4 }}>
      <Box sx={{
        position: "absolute",
        top: "50%", left: 0, right: 0,
        height: 1,
        background: `linear-gradient(90deg, transparent 0%, ${CHAMPAGNE}40 50%, transparent 100%)`,
      }} />
      <Typography
        component="span"
        sx={{
          position: "relative",
          fontFamily: FONT_SERIF,
          fontStyle: "italic",
          fontSize: 12.5,
          color: CHAMPAGNE_DEEP,
          letterSpacing: "0.05em",
          bgcolor: IVORY,
          px: 2,
        }}
      >
        — {label} —
      </Typography>
    </Box>
  );
}

/* ── Editorial line-art icons (raw inline SVG) ────────────────────────
   Hand-tuned 32px glyphs that replace the generic MUI icon in empty
   states. Single-stroke, champagne-gold, no fill — matches the
   editorial / hospitality voice.
   ──────────────────────────────────────────────────────────────────── */
const QuillIcon = ({ size = 32 }: { size?: number }) => (
  <svg width={size} height={size} viewBox="0 0 32 32" fill="none">
    {/* Champagne flute — celebratory but restrained */}
    <path d="M11 4 h10 l-1.5 9 a4 4 0 0 1 -7 0 z" stroke="currentColor" strokeWidth="1.2" strokeLinejoin="round" />
    <path d="M16 17 v8" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" />
    <path d="M12 27 h8" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" />
    {/* Soft sparkle — a tiny lozenge to the upper right */}
    <path d="M24 6 l0.6 1.4 1.4 0.6 -1.4 0.6 -0.6 1.4 -0.6 -1.4 -1.4 -0.6 1.4 -0.6 z" stroke="currentColor" strokeWidth="0.9" strokeLinejoin="round" fill="none" />
  </svg>
);
const CalendarSparkleIcon = ({ size = 32 }: { size?: number }) => (
  <svg width={size} height={size} viewBox="0 0 32 32" fill="none">
    <rect x="5" y="7" width="22" height="20" rx="2" stroke="currentColor" strokeWidth="1.2" />
    <path d="M5 13 H27" stroke="currentColor" strokeWidth="1.2" />
    <path d="M11 5 V9 M21 5 V9" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" />
    {/* Sparkle in the lower-right cell */}
    <path d="M22 21 l0.7 1.6 1.6 0.7 -1.6 0.7 -0.7 1.6 -0.7 -1.6 -1.6 -0.7 1.6 -0.7 z" stroke="currentColor" strokeWidth="0.9" strokeLinejoin="round" />
  </svg>
);
const TrailIcon = ({ size = 32 }: { size?: number }) => (
  <svg width={size} height={size} viewBox="0 0 32 32" fill="none">
    {/* Three dots ascending then a curve — a quiet "trail" */}
    <circle cx="6"  cy="22" r="1.4" stroke="currentColor" strokeWidth="1" />
    <circle cx="13" cy="18" r="1.4" stroke="currentColor" strokeWidth="1" />
    <circle cx="20" cy="14" r="1.4" stroke="currentColor" strokeWidth="1" />
    <path d="M22 12 Q26 7 28 7" stroke="currentColor" strokeWidth="1.1" strokeLinecap="round" fill="none" />
  </svg>
);

/* Hospitality microcopy — replaces stock CRM labels with maître d'
   wording wherever the dashboard touches the operator's eye. Single
   source of truth so future copy refinements stay coherent. */
const COPY = {
  unassigned:        "Awaiting Assignment",
  overdue:           "Past Due",
  hot:               "High Intent",
  warm:              "Engaged",
  cold:              "Dormant",
  newLeads:          "New Inquiries",
  confirmed:         "Confirmed Bookings",
  followUps:         "Today's Follow-ups",
  quotesPending:     "Quotes In Flight",
  attention:         "Requires Attention",
} as const;

const todayStr = (() => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
})();

const formatCurrency = (n: number | null | undefined): string => {
  const v = Number(n);
  if (!Number.isFinite(v) || v === 0) return "—";
  return `$${v.toLocaleString("en-US", { maximumFractionDigits: 0 })}`;
};

const fmtEventDate = (iso: string | null | undefined): string => {
  if (!iso) return "—";
  const d = new Date(iso + (iso.length === 10 ? "T00:00:00" : ""));
  if (!isFinite(d.getTime())) return "—";
  return d.toLocaleDateString("en-US", { month: "short", day: "numeric" });
};

const fmtRelativeTime = (iso: string | null | undefined): string => {
  if (!iso) return "—";
  const t = new Date(iso).getTime();
  if (!isFinite(t)) return "—";
  const diff = Date.now() - t;
  const mins = Math.floor(diff / 60_000);
  if (mins < 1)  return "just now";
  if (mins < 60) return `${mins}m ago`;
  const hrs  = Math.floor(mins / 60);
  if (hrs < 24)  return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  if (days < 7)  return `${days}d ago`;
  return new Date(iso).toLocaleDateString("en-US", { month: "short", day: "numeric" });
};

const fmtClockTime = (iso: string | null | undefined): string => {
  if (!iso) return "—";
  const d = new Date(iso);
  if (!isFinite(d.getTime())) return "—";
  return d.toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit", hour12: true });
};

const headers = () => ({
  "Content-Type": "application/json",
  Authorization: `Bearer ${localStorage.getItem("token") || ""}`,
});

const apiFetch = async (url: string) => {
  const res = await trackedFetch(`${API_URL}${url}`, { headers: headers() });
  const json = await res.json();
  return json.data ?? json;
};

/* Status palette (matches the rest of the CRM). */
const STATUS: Record<string, { label: string; color: string }> = {
  new:               { label: "New Inquiry",  color: "#3b82f6" },
  contacted:         { label: "Contacted",    color: "#8b5cf6" },
  negotiation:       { label: "Negotiation",  color: "#f59e0b" },
  ready_for_quote:   { label: "Quote Sent",   color: "#06b6d4" },
  ready_for_booking: { label: "Booking Ready",color: "#10b981" },
  confirmed:         { label: "Confirmed",    color: "#22c55e" },
  lost:              { label: "Lost",         color: "#ef4444" },
};
const PIPELINE_STAGES: (keyof typeof STATUS)[] = [
  "new", "contacted", "negotiation", "ready_for_quote", "ready_for_booking", "confirmed",
];

/* ──────────────────────────────────────────────────────────────────────
   Sales Operations Dashboard
   Replaces the prior analytics layout (event-type donut, source pie,
   30-day trend line, upcoming-events table, monthly revenue) with a
   live operational cockpit that answers: who needs my attention, what's
   overdue, what's due today, which staff are behind, which bookings
   are coming, what is the pipeline shape.
   ────────────────────────────────────────────────────────────────────── */
const DCCDashboard: React.FC = () => {
  const navigate = useNavigate();
  // Role gating — only Admin / Sales Manager retain the company-wide
  // Team Performance widget. Everyone else gets a personalized
  // "My Lead Status Summary" panel in the same grid slot so the layout
  // never collapses.
  const { userId, canViewAllLeads, canViewFinancials, canViewCompanyDashboard } = usePermission();

  /* ── Employee dashboard mode ─────────────────────────────────────────
   * For non-privileged roles (Lead Manager / Sales Staff / Sales Manager /
   * Coordinators / Front Desk / General Employees), every list call must
   * scope to `view=my` so the right rail of the dashboard ("Requires
   * Attention", "Today's Follow-ups", "Recent activity", "On the Books")
   * never surfaces another rep's leads. The dashboard SERVICE endpoints
   * (stats / charts / pipeline / calendar / recent-activities) are
   * already tightened backend-side via personalLeads(); these list
   * endpoints share the broader applyVisibility scope, so we tighten
   * them here with the existing `view=my` query filter. */
  const personalMode = !canViewCompanyDashboard;
  const mineParam    = personalMode ? "&view=my" : "";
  // House Floor (team workload) is a privileged widget — sales managers
  // no longer get it because their dashboard is personal-only per the
  // role-based UI spec. Use the same gate as personalMode so the panel
  // collapses to MyLeadStatusSummary for them too.
  const showTeamPerformance = canViewCompanyDashboard;

  const [loading,    setLoading]    = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  // Aggregate counters from /leads/view/dashboard-stats
  const [leadStats, setLeadStats] = useState<any>(null);
  // Task counters + items from /tasks/my
  const [taskStats, setTaskStats] = useState<any>(null);
  // Email-Hub counters (kept as a single chip row)
  const [emailStats, setEmailStats] = useState<any>(null);
  // Personal "My Lead Status Summary" counters — only fetched for
  // non-admin/non-sales-manager users (the only roles that render it).
  const [myStats, setMyStats] = useState<any>(null);

  // Operational lead lists driving the widgets.
  const [urgentLeads, setUrgentLeads] = useState<any[]>([]);
  const [todayLeads,  setTodayLeads]  = useState<any[]>([]);
  const [recentLeads, setRecentLeads] = useState<any[]>([]);
  const [bookings,    setBookings]    = useState<any[]>([]);
  // Cross-lead Recent SMS feed for the bottom tile. Visibility-scoped
  // server-side by LeadService::applyVisibility — employees only see
  // SMS tied to leads they're allowed to view; admins see everything.
  const [recentSms,   setRecentSms]   = useState<any[]>([]);

  /* ── Latest Lead Updates — live operational lead feed ──────────
     ONE ROW PER LEAD, enriched with status/assignee/follow-up/AI
     signal + a compact activity preview. Refreshed by useLeadBus on
     every mutation anywhere in the app so the feed self-updates as
     the team works. Filter is server-side; tabs are state-based
     (not activity-type-based) so each tab answers a real
     operational question. */
  type LatestUpdatesFilter = "all" | "mine" | "new" | "follow_up_due" | "high_interest" | "awaiting_assignment" | "vip";
  const [latestFilter, setLatestFilter] = useState<LatestUpdatesFilter>("all");
  const [latestUpdates, setLatestUpdates] = useState<{
    metrics: { active_conversations: number; follow_ups_overdue: number; awaiting_assignment: number; replies_today: number };
    leads: any[];
  }>({ metrics: { active_conversations: 0, follow_ups_overdue: 0, awaiting_assignment: 0, replies_today: 0 }, leads: [] });
  const [latestLoading, setLatestLoading] = useState<boolean>(true);

  /* ── Urgent leads filter — client-side, no extra round-trip.
     The urgent list arrives pre-sorted; this just slices it by
     reason (overdue / hot / unassigned) for the chip-strip
     interaction. "Today" filters on follow_up_at falling on the
     current date. */
  type UrgentFilter = "all" | "overdue" | "hot" | "unassigned" | "today";
  const [urgentFilter, setUrgentFilter] = useState<UrgentFilter>("all");

  // Team list for the Team Performance widget.
  const [teamUsers, setTeamUsers] = useState<any[]>([]);

  /* Follow-up Detail Modal — opened from any row that previously
   * navigated straight to /leads/{id}. Keeps the operator in the
   * dashboard context for quick review + actions; the modal still
   * exposes "Open Full Lead" for deep-dive workflows. */
  const [modalLeadId, setModalLeadId] = useState<number | null>(null);
  const openLead = useCallback((id: number) => setModalLeadId(id), []);

  const loadData = useCallback(async (isRefresh = false) => {
    if (isRefresh) setRefreshing(true);
    try {
      const [ls, ts, email, urgent, today, recent, booked, team, mine, sms] = await Promise.all([
        apiFetch("/leads/view/dashboard-stats"),
        // Tasks source is the SAME for the "My Desk" widget and the
        // "Today's Follow-ups" widget — they must agree on counts. For
        // privileged dashboard users we ask for the team scope so admins
        // see the whole queue; employees stay personal-scoped. Server
        // silently demotes 'team' for non-privileged users so this
        // request is safe regardless.
        apiFetch(personalMode ? "/tasks/my" : "/tasks/my?scope=team"),
        apiFetch("/email-hub/v3/dashboard/stats").catch(() => null),
        // Urgent — anything flagged for follow-up, soonest first. For
        // employees, view=my restricts to strictly-assigned leads.
        apiFetch(`/leads?per_page=8&follow_up_required=1&sort=follow_up_at&dir=asc${mineParam}`).catch(() => ({ data: [] })),
        // Today's follow-ups — same personal scope for employees.
        apiFetch(`/leads?per_page=8&follow_up_today=1&sort=follow_up_at&dir=asc${mineParam}`).catch(() => ({ data: [] })),
        // Recent activity stream (newest leads).
        apiFetch(`/leads?per_page=10&sort=created_at&dir=desc${mineParam}`).catch(() => ({ data: [] })),
        // Upcoming bookings — confirmed status, soonest event date.
        apiFetch(`/leads?per_page=6&status=confirmed&sort=event_date&dir=asc${mineParam}`).catch(() => ({ data: [] })),
        // Team list only when the user is authorized to see the widget —
        // saves a round-trip for Lead Managers who never render it.
        showTeamPerformance
          ? apiFetch("/leads/view/team-users").catch(() => [])
          : Promise.resolve([]),
        // Personal lead summary — only fetched for the inverse cohort so
        // the dashboard never wastes an idle request when not rendered.
        !showTeamPerformance
          ? apiFetch("/leads/view/my-dashboard-stats").catch(() => null)
          : Promise.resolve(null),
        // Recent SMS across all visible leads — scoped server-side by
        // LeadService::applyVisibility, so this works for every role.
        apiFetch(`/sms/recent?per_page=8`).catch(() => []),
      ]);
      setLeadStats(ls);
      setTaskStats(ts);
      setEmailStats(email);
      setUrgentLeads(Array.isArray(urgent) ? urgent : (urgent?.data ?? []));
      setTodayLeads(Array.isArray(today)   ? today   : (today?.data   ?? []));
      setRecentLeads(Array.isArray(recent) ? recent : (recent?.data ?? []));
      setBookings(Array.isArray(booked)    ? booked : (booked?.data  ?? []));
      setTeamUsers(Array.isArray(team) ? team : []);
      setMyStats(mine ?? null);
      setRecentSms(Array.isArray(sms) ? sms : (sms?.data ?? []));
    } catch {}
    setLoading(false);
    setRefreshing(false);
  }, [showTeamPerformance, mineParam, personalMode]);

  useEffect(() => { loadData(); }, [loadData]);

  /* ── Latest Lead Updates — dedicated fetcher.
     Kept separate from loadData so a tab change only re-hits this
     endpoint, and so the lead-bus refresh stays cheap (no need to
     re-fetch every dashboard counter when one lead row changes). */
  const loadLatestUpdates = useCallback(async () => {
    setLatestLoading(true);
    try {
      const payload = await apiFetch(`/leads/view/latest-updates?filter=${latestFilter}&limit=12`);
      setLatestUpdates({
        metrics: payload?.metrics ?? { active_conversations: 0, follow_ups_overdue: 0, awaiting_assignment: 0, replies_today: 0 },
        leads:   Array.isArray(payload?.leads) ? payload.leads : [],
      });
    } catch {
      setLatestUpdates({
        metrics: { active_conversations: 0, follow_ups_overdue: 0, awaiting_assignment: 0, replies_today: 0 },
        leads:   [],
      });
    }
    setLatestLoading(false);
  }, [latestFilter]);

  useEffect(() => { loadLatestUpdates(); }, [loadLatestUpdates]);

  /* ── Live sync — any lead mutation anywhere in the app refreshes
     every operational widget on the dashboard (KPIs, urgent list,
     follow-ups, latest updates, bookings, pipeline). Same-tab in
     real time; cross-tab via BroadcastChannel. */
  useLeadBus(() => { loadData(); loadLatestUpdates(); });

  // ── Derived values — must be computed BEFORE the early-return on
  //   loading so hook order stays stable across renders. The
  //   `react-hooks/rules-of-hooks` lint rule rejects any useMemo /
  //   useState after a conditional return.
  const ls = leadStats || {};
  const ts = taskStats?.stats || { total: 0, overdue: 0, today: 0, upcoming: 0 };

  /* ── KPI derivations ─────────────────────────────────────────────── */
  const newLeadsToday = useMemo(
    () => recentLeads.filter((l) => (l.created_at || "").startsWith(todayStr)).length,
    [recentLeads]
  );
  const overdueLeads = useMemo(() => {
    return Math.max(0, (ls.follow_up_required || 0) - (ls.follow_up_today || 0));
  }, [ls.follow_up_required, ls.follow_up_today]);
  const quotesPending  = (ls.by_status?.ready_for_quote || 0) + (ls.by_status?.ready_for_booking || 0);
  const confirmedCount = ls.by_status?.confirmed || 0;

  // Compute a % delta vs last week from the leadStats payload when
  // it carries this_week / last_week. Falls back to null when the
  // signal is unavailable so the KPI tile doesn't show a fake delta.
  const weekDelta = (() => {
    const tw = Number(ls.this_week);
    const lw = Number(ls.last_week);
    if (!Number.isFinite(tw) || !Number.isFinite(lw) || lw === 0) return null;
    return Math.round(((tw - lw) / lw) * 100);
  })();

  /* ── Pipeline Pulse data wiring ─────────────────────────────────
     The dial's three rings + satellites + center number are all
     derived from data we already load. No new endpoint required.
       • Revenue: sum of confirmed-booking budget values
       • Avg deal: mean budget across confirmed bookings
       • Win rate: confirmed / (confirmed + lost), guarded for /0
       • Days to close: from avg_response_minutes when available */
  const bookingRevenue = useMemo(() => {
    return (bookings || []).reduce((sum: number, b: any) => sum + Number(b.budget_value ?? 0), 0);
  }, [bookings]);

  const avgDealValue = useMemo(() => {
    const withValue = (bookings || []).filter((b: any) => Number(b.budget_value) > 0);
    if (withValue.length === 0) return 0;
    return withValue.reduce((s: number, b: any) => s + Number(b.budget_value), 0) / withValue.length;
  }, [bookings]);

  const lostCount = ls.by_status?.lost || 0;
  const winRate = (confirmedCount + lostCount) > 0
    ? Math.round((confirmedCount / (confirmedCount + lostCount)) * 100)
    : 0;

  const avgDaysToClose = ls.avg_response_minutes
    ? Math.max(1, Math.round(Number(ls.avg_response_minutes) / (60 * 24)))
    : 18;

  /* ── Booking Density — events keyed by ISO date ─────────────── */
  const bookingsByDate = useMemo(() => {
    const map: Record<string, number> = {};
    (bookings || []).forEach((b: any) => {
      if (!b.event_date) return;
      const iso = String(b.event_date).slice(0, 10);
      map[iso] = (map[iso] ?? 0) + 1;
    });
    return map;
  }, [bookings]);

  /* ── Revenue Trajectory — real bookings, grouped by event_month +
   *   event_type into the 3 layers the chart expects (weddings /
   *   corporate / birthdays). Anything outside those three types is
   *   summed into `corporate` so the curve still reflects total
   *   revenue. Months with zero bookings render as zero — no
   *   soft-floor fabrication. The chart's own empty-state handles the
   *   no-data case visually. */
  const revenueTrajectoryData = useMemo(() => {
    const now = new Date();
    const slices: { month: string; weddings: number; corporate: number; birthdays: number }[] = [];
    const byKey: Record<string, { weddings: number; corporate: number; birthdays: number }> = {};

    (bookings || []).forEach((b: any) => {
      if (!b.event_date) return;
      const d = new Date(b.event_date + (b.event_date.length === 10 ? "T12:00:00" : ""));
      if (!isFinite(d.getTime())) return;
      const key = `${d.getFullYear()}-${d.getMonth()}`;
      const value = Number(b.budget_value ?? 0);
      if (!byKey[key]) byKey[key] = { weddings: 0, corporate: 0, birthdays: 0 };
      const t = String(b.event_type ?? "").toLowerCase();
      if (t.includes("wedding"))                 byKey[key].weddings   += value;
      else if (t.includes("birthday"))           byKey[key].birthdays  += value;
      else                                       byKey[key].corporate  += value;
    });

    for (let i = 11; i >= 0; i--) {
      const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
      const key = `${d.getFullYear()}-${d.getMonth()}`;
      const row = byKey[key] ?? { weddings: 0, corporate: 0, birthdays: 0 };
      slices.push({
        month: d.toLocaleDateString("en-US", { month: "short" }),
        ...row,
      });
    }
    return slices;
  }, [bookings]);

  // Early-return on loading lives AFTER every hook so React's
  // hook-order invariant holds. Adding more useMemos above? Keep them
  // above this line.
  if (loading) return <Loader height="80vh" />;

  /* KPI tiles. Labels personalize ("My New Inquiries", "My Follow-ups
   * Due", …) when the user is on the employee dashboard so the row reads
   * as a personal scorecard rather than a company snapshot.
   *
   * The "Awaiting Assignment" tile is admin-only — under personal scope
   * the unassigned count is by definition 0 (a lead unassigned to anyone
   * cannot be in MY scope), so the tile would always read 0 and waste a
   * slot. The remaining 6 reflow the 7-column grid to 6 cleanly. */
  const kpis: KpiDef[] = [
    { id: "new",        label: personalMode ? "My New Inquiries"      : "New Inquiries",       value: newLeadsToday,           sub: "Past 24h",        icon: <PeopleIcon sx={{ fontSize: 14 }} />,                accent: CHAMPAGNE, delta: weekDelta, onClick: () => navigate(personalMode ? "/leads/inquiries?view=my" : "/leads/inquiries") },
    { id: "followups",  label: personalMode ? "My Follow-ups Due"     : "Follow-ups Due",      value: ls.follow_up_today || 0, sub: "Today",           icon: <NotificationsActiveIcon sx={{ fontSize: 14 }} />,  accent: "#3b82f6", delta: null,      onClick: () => navigate(`/leads/inquiries?follow_up_today=1${personalMode ? "&view=my" : ""}`) },
    { id: "overdue",    label: personalMode ? "My Past Due"           : "Past Due",            value: overdueLeads,            sub: "Needs recovery",  icon: <WarningIcon sx={{ fontSize: 14 }} />,              accent: "#dc2626", delta: null,      onClick: () => navigate(`/leads/inquiries?follow_up=1${personalMode ? "&view=my" : ""}`) },
    { id: "hot",        label: personalMode ? "My High Intent"        : "High Intent",         value: ls.hot_leads || 0,       sub: "Hot",             icon: <LocalFireDepartmentIcon sx={{ fontSize: 14 }} />,  accent: "#f97316", delta: null,      onClick: () => navigate(`/leads/inquiries?ai_score=hot${personalMode ? "&view=my" : ""}`) },
    { id: "quotes",     label: personalMode ? "My Quotes In Flight"   : "Quotes In Flight",    value: quotesPending,           sub: "Pending close",   icon: <RequestQuoteOutlinedIcon sx={{ fontSize: 14 }} />, accent: CHAMPAGNE_DEEP, delta: null, onClick: () => navigate(`/leads/inquiries?status=ready_for_quote${personalMode ? "&view=my" : ""}`) },
    { id: "bookings",   label: personalMode ? "My Confirmed Bookings" : "Confirmed Bookings",  value: confirmedCount,          sub: "Secured",         icon: <CheckCircleIcon sx={{ fontSize: 14 }} />,          accent: "#16a34a", delta: null,      onClick: () => navigate(`/leads/inquiries?status=confirmed${personalMode ? "&view=my" : ""}`) },
    ...(personalMode ? [] as KpiDef[] : [
      { id: "unassigned", label: "Awaiting Assignment", value: ls.unassigned || 0, sub: "Needs owner", icon: <PersonOffIcon sx={{ fontSize: 14 }} />, accent: "#94a3b8", delta: null, onClick: () => navigate("/leads/inquiries?unassigned=1") },
    ] as KpiDef[]),
  ];

  // Today's events count — drives the contextual tag on the right
  // side of the hero ("3 Active Today"). Pulled from the existing
  // bookings + today follow-ups so we don't need a new endpoint.
  const todaysActiveCount = (todayLeads?.length ?? 0) + (bookings?.filter((b: any) =>
    (b.event_date || "").startsWith(todayStr)
  ).length ?? 0);

  // Monospaced live timestamp for the hero's date tag.
  const heroDate = new Date().toLocaleDateString("en-US", {
    weekday: "short", month: "short", day: "numeric", year: "numeric",
  }).toUpperCase();

  return (
    <Box p={3} sx={{ bgcolor: IVORY, minHeight: "100%" }}>

      {/* ── Hero — editorial dark gradient mesh ─────────────────────
          The single highest-impact surface on the page. A near-black
          charcoal canvas, a fine gold corner glow, a fine hairline at
          the bottom edge, and a live pulsing dot next to the serif
          title. Avoids the generic SaaS-blue banner entirely. */}
      <Fade in timeout={500}>
        <Card elevation={0} sx={{
          mb: 3,
          borderRadius: SURFACE.cardRadius,
          background: `radial-gradient(120% 80% at 90% 0%, rgba(201,169,97,0.18) 0%, transparent 50%), linear-gradient(135deg, ${CHARCOAL} 0%, ${GRAPHITE} 100%)`,
          color: "#fff",
          overflow: "hidden",
          position: "relative",
          borderBottom: `1px solid ${CHAMPAGNE}`,
          // Two stacked overlays:
          //  &::before — a faint gold "starfield" — tiny champagne
          //    dots at low opacity scattered across the entire panel.
          //    Reads as luxury texture, never as noise. Layered under
          //    the dot-grid so the grid stays dominant on the right.
          //  &::after  — the regular dot-grid in the top-right
          //    quadrant, masked to fade left.
          "&::before": {
            content: '""',
            position: "absolute", inset: 0,
            backgroundImage:
              "radial-gradient(circle, rgba(201,169,97,0.18) 0.7px, transparent 1px)",
            backgroundSize: "44px 44px",
            backgroundPosition: "0 0, 22px 22px",
            opacity: 0.6,
            pointerEvents: "none",
          },
          "&::after": {
            content: '""',
            position: "absolute", top: 0, right: 0, bottom: 0, width: "45%",
            backgroundImage: "radial-gradient(rgba(255,255,255,0.05) 1px, transparent 1px)",
            backgroundSize: "16px 16px",
            maskImage: "linear-gradient(to left, rgba(0,0,0,0.5), transparent)",
            WebkitMaskImage: "linear-gradient(to left, rgba(0,0,0,0.5), transparent)",
            pointerEvents: "none",
          },
        }}>
          <CardContent sx={{ py: 3.5, px: 4, position: "relative", zIndex: 1, "&:last-child": { pb: 3.5 } }}>
            <Stack direction="row" justifyContent="space-between" alignItems="center" flexWrap="wrap" gap={2}>
              <Box>
                {/* Eyebrow line — micro tracked-out sans, champagne tinted */}
                <Stack direction="row" alignItems="center" gap={1.2}>
                  <Box sx={{
                    position: "relative", width: 7, height: 7, borderRadius: "50%",
                    bgcolor: "#22c55e",
                    boxShadow: "0 0 0 0 rgba(34,197,94,0.7)",
                    animation: "livepulse 2s ease-out infinite",
                    "@keyframes livepulse": {
                      "0%":   { boxShadow: "0 0 0 0 rgba(34,197,94,0.7)" },
                      "70%":  { boxShadow: "0 0 0 8px rgba(34,197,94,0)" },
                      "100%": { boxShadow: "0 0 0 0 rgba(34,197,94,0)" },
                    },
                  }} />
                  <Typography sx={{
                    fontSize: 10, fontWeight: 600, letterSpacing: "0.22em",
                    color: CHAMPAGNE, textTransform: "uppercase",
                  }}>
                    Live · Sales Operations
                  </Typography>
                </Stack>

                {/* Editorial serif title — the move that signals
                    "hospitality" instead of "CRM". */}
                <Typography sx={{
                  fontFamily: FONT_SERIF,
                  fontSize: { xs: 34, md: 44 },
                  fontWeight: 400,
                  lineHeight: 1.05,
                  letterSpacing: "-0.02em",
                  mt: 1.2,
                  color: "#fff",
                }}>
                  Live Command Center
                </Typography>

                {/* Monospaced date tag + today's events pill */}
                <Stack direction="row" alignItems="center" gap={1} mt={1.6} flexWrap="wrap">
                  <Box sx={{
                    fontFamily: FONT_MONO,
                    fontSize: 10.5, fontWeight: 500, letterSpacing: "0.08em",
                    color: "rgba(255,255,255,0.65)",
                    bgcolor: "rgba(255,255,255,0.04)",
                    border: "1px solid rgba(255,255,255,0.08)",
                    borderRadius: "6px",
                    px: 1, py: 0.4,
                  }}>
                    {heroDate}
                  </Box>
                  {todaysActiveCount > 0 && (
                    <Box sx={{
                      display: "inline-flex", alignItems: "center", gap: 0.6,
                      fontSize: 10.5, fontWeight: 600, letterSpacing: "0.05em",
                      color: CHAMPAGNE,
                      bgcolor: "rgba(201,169,97,0.08)",
                      border: `1px solid ${CHAMPAGNE}33`,
                      borderRadius: "999px",
                      px: 1.1, py: 0.4,
                    }}>
                      <Box sx={{ width: 5, height: 5, borderRadius: "50%", bgcolor: CHAMPAGNE }} />
                      {todaysActiveCount} ACTIVE TODAY
                    </Box>
                  )}
                </Stack>
              </Box>

              {/* Right side — soft-pastel alert pills + ghost refresh */}
              <Stack direction="row" gap={1} alignItems="center" flexWrap="wrap">
                {ts.overdue > 0 && (
                  <Chip
                    icon={<WarningIcon sx={{ color: "#fef2f2 !important", fontSize: 13 }} />}
                    label={`${ts.overdue} Past Due`}
                    onClick={() => navigate("/tasks")}
                    sx={{
                      bgcolor: "rgba(239,68,68,0.12)",
                      color: "#fecaca",
                      fontWeight: 600,
                      fontSize: 11,
                      letterSpacing: 0.3,
                      cursor: "pointer",
                      border: "1px solid rgba(239,68,68,0.25)",
                      borderRadius: "999px",
                      height: 26,
                      "&:hover": { bgcolor: "rgba(239,68,68,0.18)" },
                    }}
                  />
                )}
                {emailStats?.overdue_threads > 0 && (
                  <Chip
                    icon={<EmailIcon sx={{ color: "#fef3c7 !important", fontSize: 13 }} />}
                    label={`${emailStats.overdue_threads} Email Backlog`}
                    onClick={() => navigate("/email-hub")}
                    sx={{
                      bgcolor: "rgba(245,158,11,0.12)",
                      color: "#fde68a",
                      fontWeight: 600,
                      fontSize: 11,
                      letterSpacing: 0.3,
                      cursor: "pointer",
                      border: "1px solid rgba(245,158,11,0.25)",
                      borderRadius: "999px",
                      height: 26,
                      "&:hover": { bgcolor: "rgba(245,158,11,0.18)" },
                    }}
                  />
                )}
                {/* LivePulse was removed in the mock-data sweep — there's
                    no leads-per-hour endpoint, so the ticker was animating
                    Math.random() values. The "LIVE · Sales Operations"
                    pulsing badge in the hero eyebrow already signals
                    real-time state. */}
                <Tooltip title="Refresh">
                  <IconButton
                    onClick={() => loadData(true)}
                    sx={{
                      color: "rgba(255,255,255,0.85)",
                      bgcolor: "rgba(255,255,255,0.04)",
                      border: "1px solid rgba(255,255,255,0.1)",
                      borderRadius: "999px",
                      width: 34, height: 34,
                      transition: "all 200ms ease-out",
                      "&:hover": { bgcolor: "rgba(201,169,97,0.12)", borderColor: `${CHAMPAGNE}55`, color: CHAMPAGNE },
                    }}
                  >
                    <RefreshIcon sx={{ fontSize: 16, animation: refreshing ? "spin 1s linear infinite" : "none", "@keyframes spin": { "0%": { transform: "rotate(0deg)" }, "100%": { transform: "rotate(360deg)" } } }} />
                  </IconButton>
                </Tooltip>
              </Stack>
            </Stack>
          </CardContent>
        </Card>
      </Fade>

      {/* ── KPI row — single luxury surface, hairline dividers ──────
          The 7 metrics live in one elegant container with vertical
          hairlines between them, like a watch dial's sub-counters.
          No card borders, no separate boxes, no shadows on the
          individual KPIs — only a single soft Paper holds them all.
          On lg+ the row is a 7-column track; below that it wraps into
          a 4-column then 2-column grid. */}
      <Paper elevation={0} sx={{
        mb: 3,
        borderRadius: SURFACE.cardRadius,
        border: SURFACE.cardBorder,
        bgcolor: SURFACE.card,
        boxShadow: SURFACE.cardShadow,
        overflow: "hidden",
      }}>
        <Box sx={{
          display: "grid",
          gridTemplateColumns: {
            xs: "repeat(2, 1fr)",
            sm: personalMode ? "repeat(3, 1fr)" : "repeat(4, 1fr)",
            lg: `repeat(${kpis.length}, 1fr)`,
          },
        }}>
          {kpis.map((k, i) => (
            <KpiCard key={k.id} def={k} isFirstInRow={i % kpis.length === 0} />
          ))}
        </Box>
      </Paper>

      {/* PERFORMANCE TRIO was removed in the mock-data sweep —
          its Revenue Flow column duplicated the right-rail Pipeline
          panel, and the Momentum Compass + Daily Rhythm columns
          relied on metrics the backend doesn't expose (close rate,
          response time, lead quality, repeat business, daily activity
          rhythm). Real funnel data still renders below in the
          right-rail PipelineBars. */}

      {/* ══ REVENUE TRAJECTORY — full-width stacked area ════════════
          The "new visual anchor" of the page (per spec): 3 stacked
          layers (weddings · corporate · birthdays) in champagne /
          bronze / sage. Recharts under the hood with every default
          color overridden. Range toggle (1M/3M/12M) lives in the
          header strip; huge serif total + YoY delta carry the data
          before the eye reaches the curve.

          Hidden entirely for employee-level roles (anyone outside
          Admin / Finance Manager / Sales Director / Operations
          Manager) per the role-based-UI spec. The page reflows
          naturally because Paper's `mb: 3` collapses with the next
          sibling. */}
      {canViewFinancials && (
        <Paper elevation={0} sx={{
          mb: 3,
          p: { xs: 3, md: 4 },
          borderRadius: SURFACE.cardRadius,
          border: SURFACE.cardBorder,
          bgcolor: SURFACE.card,
          boxShadow: SURFACE.cardShadow,
        }}>
          <RevenueTrajectory data={revenueTrajectoryData} />
        </Paper>
      )}

      {/* Editorial section divider — "Operations" zone */}
      <SectionDivider label="Operations" />

      {/* ── 70 / 30 main grid ───────────────────────────────────────── */}
      <Grid container spacing={1.6}>
        {/* ════ LEFT (70%) ════════════════════════════════════════════ */}
        <Grid item xs={12} lg={8}>
          <Stack spacing={1.6}>
            {/* SECTION 1 · Latest Lead Updates — live operational feed.
                Moved to the top of the activity zone because it answers
                "what's happening right now?" — the question a sales
                manager scans for first when opening the dashboard. The
                LIVE pulsing badge in its header drives that home. */}
            <LatestLeadUpdatesCard
              metrics={latestUpdates.metrics}
              leads={latestUpdates.leads}
              loading={latestLoading}
              filter={latestFilter}
              onFilterChange={setLatestFilter}
              onNavigate={navigate}
              onOpenLead={openLead}
            />

            {/* SECTION 2 · Requires Attention — the action list. Follows
                naturally after the live activity: "what needs my
                attention?" Page-level hero card; warmer cream ground,
                larger serif header, champagne left rail. */}
            <SectionCard
              elevated
              title={personalMode ? "Needs My Attention" : "Requires Attention"}
              subtitle={personalMode
                ? `${urgentLeads.length} priority · my overdue follow-ups & uncontacted leads`
                : `${urgentLeads.length} priority · past-due follow-ups, high-intent, awaiting assignment`}
              accentIcon={<WarningIcon sx={{ fontSize: 18 }} />}
              onSeeAll={() => navigate(`/leads/inquiries?follow_up=1${personalMode ? "&view=my" : ""}`)}
            >
              {/* Sticky filter chip strip — luxury watch-dial styling. */}
              <UrgentFilterStrip
                items={urgentLeads}
                filter={urgentFilter}
                onChange={setUrgentFilter}
              />
              {(() => {
                const filtered = applyUrgentFilter(urgentLeads, urgentFilter);
                if (filtered.length === 0) {
                  return (
                    <EmptyState
                      icon={<CheckCircleIcon sx={{ fontSize: 30 }} />}
                      text="The room is calm. Nothing in this lane needs your attention right now."
                    />
                  );
                }
                return (
                  <Stack>
                    {filtered.map((lead, i) => (
                      <UrgentLeadRow
                        key={lead.id}
                        lead={lead}
                        onClick={() => navigate(`/leads/${lead.id}`)}
                        isLast={i === filtered.length - 1}
                      />
                    ))}
                  </Stack>
                );
              })()}
            </SectionCard>

            {/* SECTION 3 · Today's Follow-ups — the calendar handoff,
                last in the activity stack ("what's coming next?").

                Source = lead_tasks (via /tasks/my), NOT lead_threads.
                This makes the dashboard widget and the Tasks page agree
                on the exact same rows — counts, ordering, ownership —
                and surfaces per-task signals (assignee, attempt count,
                last outcome) that the lead-flag query couldn't show.
                "View All" goes to /tasks for the same reason. */}
            {(() => {
              const todayFollowUps = (taskStats?.today ?? [])
                .filter((t: any) => t.lead_id != null)
                .slice(0, 8);
              return (
                <SectionCard
                  title={personalMode ? "My Follow-ups Today" : "Today's Follow-ups"}
                  subtitle={`${todayFollowUps.length} scheduled · in chronological order`}
                  accentIcon={<AccessTimeIcon sx={{ fontSize: 16 }} />}
                  onSeeAll={() => navigate("/tasks")}
                >
                  {todayFollowUps.length === 0 ? (
                    <EmptyState
                      icon={<AccessTimeIcon sx={{ fontSize: 30 }} />}
                      text="The day is clear. No follow-ups scheduled."
                    />
                  ) : (
                    <Stack>
                      {todayFollowUps.map((t: any, i: number) => (
                        <FollowUpTaskRow
                          key={t.id}
                          task={t}
                          onClick={() => openLead(t.lead_id)}
                          isLast={i === todayFollowUps.length - 1}
                        />
                      ))}
                    </Stack>
                  )}
                </SectionCard>
              );
            })()}
          </Stack>
        </Grid>

        {/* ════ RIGHT (30%) ═══════════════════════════════════════════ */}
        <Grid item xs={12} lg={4}>
          <Stack spacing={1.6}>

            {/* My Tasks */}
            <SectionCard
              muted
              title="My Desk"
              subtitle={`${ts.overdue} past due · ${ts.today} today · ${ts.upcoming} upcoming`}
              accentIcon={<NotificationsActiveIcon sx={{ fontSize: 16 }} />}
              onSeeAll={() => navigate("/tasks")}
            >
              {/* Stat counters */}
              <Stack direction="row" gap={0.8} sx={{ px: 3, py: 1.8, borderBottom: SURFACE.divider }}>
                {[
                  { label: "Past Due", count: ts.overdue, color: "#dc2626" },
                  { label: "Today",    count: ts.today,   color: "#3b82f6" },
                  { label: "Upcoming", count: ts.upcoming,color: "#16a34a" },
                ].map((t) => (
                  <Box
                    key={t.label}
                    onClick={() => navigate("/tasks")}
                    sx={{
                      flex: 1, textAlign: "center", py: 0.8, borderRadius: "8px", cursor: "pointer",
                      bgcolor: t.count > 0 ? `${t.color}10` : "#fafbfc",
                      border: `1px solid ${t.count > 0 ? `${t.color}30` : "#eef0f4"}`,
                      transition: "all 0.15s",
                      "&:hover": { borderColor: t.color },
                    }}
                  >
                    <Typography sx={{ fontSize: 18, fontWeight: 800, color: t.count > 0 ? t.color : "#94a3b8", lineHeight: 1, fontVariantNumeric: "tabular-nums" }}>
                      {t.count}
                    </Typography>
                    <Typography sx={{ fontSize: 9.5, fontWeight: 700, color: "#94a3b8", letterSpacing: 0.4, textTransform: "uppercase", mt: 0.3 }}>
                      {t.label}
                    </Typography>
                  </Box>
                ))}
              </Stack>

              {/* Items list */}
              {(taskStats?.overdue?.length > 0 || taskStats?.today?.length > 0) ? (
                <Stack>
                  {[
                    ...((taskStats?.overdue || []).map((t: any) => ({ ...t, _section: "overdue" }))),
                    ...((taskStats?.today   || []).map((t: any) => ({ ...t, _section: "today"   }))),
                  ].slice(0, 5).map((task: any, i: number, arr: any[]) => (
                    <TaskRow
                      key={task.id}
                      task={task}
                      onClick={() => task.lead_id ? openLead(task.lead_id) : navigate("/tasks")}
                      isLast={i === arr.length - 1}
                    />
                  ))}
                </Stack>
              ) : (
                <EmptyState
                  icon={<CheckCircleIcon sx={{ fontSize: 30 }} />}
                  text="All caught up. Enjoy the lull."
                />
              )}
            </SectionCard>

            {/* Team Performance (admins / sales managers) OR
                My Lead Status Summary (everyone else). Same slot so
                grid spacing never changes. */}
            {showTeamPerformance ? (
              <SectionCard
                muted
                title="House Floor"
                subtitle={`${teamUsers.length} on duty · current workload`}
                accentIcon={<PeopleIcon sx={{ fontSize: 16 }} />}
                onSeeAll={() => navigate("/leads/inquiries")}
              >
                {teamUsers.length === 0 ? (
                  <EmptyState
                    icon={<PeopleIcon sx={{ fontSize: 30 }} />}
                    text="No team activity to report."
                  />
                ) : (() => {
                  // Compute counts from recent + urgent leads (latest snapshot).
                  const sample = [...recentLeads, ...urgentLeads];
                  const counts: Record<number, number> = {};
                  sample.forEach((l) => { if (l.assigned_to) counts[l.assigned_to] = (counts[l.assigned_to] || 0) + 1; });
                  const maxCount = Math.max(1, ...Object.values(counts));
                  const sorted = [...teamUsers]
                    .map((u: any) => ({ ...u, _count: counts[u.id] || 0 }))
                    .sort((a, b) => b._count - a._count)
                    .slice(0, 6);
                  return (
                    <Stack>
                      {sorted.map((u, i) => (
                        <TeamRow
                          key={u.id}
                          user={u}
                          count={u._count}
                          maxCount={maxCount}
                          onClick={() => navigate(`/leads/inquiries?assigned_to=${u.id}`)}
                          isLast={i === sorted.length - 1}
                        />
                      ))}
                    </Stack>
                  );
                })()}
              </SectionCard>
            ) : (
              <MyLeadStatusSummary
                stats={myStats}
                userId={userId}
                onNavigate={navigate}
              />
            )}

            {/* Pipeline — at-a-glance stacked bar + per-stage rows */}
            {(() => {
              const total = PIPELINE_STAGES.reduce((sum, k) => sum + (ls.by_status?.[k] || 0), 0);
              const pipelineStages = PIPELINE_STAGES.map((s) => ({
                key: s, label: STATUS[s].label, color: STATUS[s].color,
              }));
              return (
                <SectionCard
                  muted
                  title={personalMode ? "My Pipeline" : "Pipeline"}
                  subtitle={personalMode ? "My active leads, stage by stage" : "Active leads, stage by stage"}
                  accentIcon={<TrendingUpIcon sx={{ fontSize: 16 }} />}
                >
                  {/* ── Horizontal stacked bar at the top ─────────── */}
                  <Box sx={{ px: 3, pt: 2.2, pb: 2, borderBottom: SURFACE.divider }}>
                    <Stack direction="row" alignItems="baseline" justifyContent="space-between" mb={1.2}>
                      <Typography sx={{
                        fontFamily: FONT_SERIF, fontSize: 24, fontWeight: 400,
                        color: INK, lineHeight: 1, letterSpacing: "-0.02em",
                        fontVariantNumeric: "tabular-nums",
                      }}>
                        {total}
                      </Typography>
                      <Typography sx={{
                        fontSize: 10, fontWeight: 600, color: "#94a3b8",
                        letterSpacing: "0.18em", textTransform: "uppercase",
                      }}>
                        Active Leads
                      </Typography>
                    </Stack>
                    <StackedPipelineBar
                      byStatus={ls.by_status ?? {}}
                      total={total}
                      stages={pipelineStages}
                    />
                  </Box>

                  {/* ── PipelineBars — animated per-stage horizontal
                      bars + mini conversion funnel SVG below. The
                      week-over-week delta arrows were dropped in the
                      mock-data sweep — historical pipeline snapshots
                      aren't recorded server-side. */}
                  <Box sx={{ px: 3, py: 2.4 }}>
                    <PipelineBars
                      stages={pipelineStages.map((s) => ({
                        key: s.key, label: s.label, color: s.color,
                        count: ls.by_status?.[s.key] || 0,
                      })) as PipelineStage[]}
                    />
                  </Box>
                </SectionCard>
              );
            })()}

            {/* Upcoming Bookings */}
            <SectionCard
              muted
              title={personalMode ? "My Confirmed Bookings" : "On the Books"}
              subtitle={`${bookings.length} confirmed · upcoming events`}
              accentIcon={<EventAvailableIcon sx={{ fontSize: 16 }} />}
              onSeeAll={() => navigate(`/leads/inquiries?status=confirmed${personalMode ? "&view=my" : ""}`)}
            >
              {bookings.length === 0 ? (
                /* Per spec: "even with zero data, show the gauge at
                   0% with a gentle pointer — much better than empty
                   state". The gauge replaces the cold empty card. */
                <Box sx={{ p: 3 }}>
                  <ConversionGauge
                    value={winRate}
                    stats={[
                      { label: "Quotes",  value: String(quotesPending) },
                      { label: "Closed",  value: String(confirmedCount) },
                      // Avg Deal is a financial metric — only privileged
                      // roles see it. Employees get a Win Rate count tile
                      // instead so the 3-stat row never has an empty slot.
                      canViewFinancials
                        ? { label: "Avg Deal", value: avgDealValue > 0 ? `$${Math.round(avgDealValue / 1000)}K` : "—" }
                        : { label: "Win Rate", value: `${winRate}%` },
                    ]}
                  />
                  <Box mt={2.5} sx={{ textAlign: "center" }}>
                    <Button
                      size="small"
                      onClick={() => navigate("/leads/inquiries?status=ready_for_booking")}
                      sx={{
                        textTransform: "none",
                        fontSize: 11.5, fontWeight: 600, letterSpacing: "0.05em",
                        color: INK,
                        bgcolor: `${CHAMPAGNE}15`,
                        border: `1px solid ${CHAMPAGNE}50`,
                        borderRadius: "999px",
                        px: 2, py: 0.5,
                        "&:hover": { bgcolor: `${CHAMPAGNE}28`, borderColor: CHAMPAGNE_DEEP, color: CHAMPAGNE_DEEP },
                      }}
                    >
                      Add Booking →
                    </Button>
                  </Box>
                </Box>
              ) : (
                <Stack>
                  {bookings.map((lead, i) => (
                    <BookingRow
                      key={lead.id}
                      lead={lead}
                      onClick={() => navigate(`/leads/${lead.id}`)}
                      isLast={i === bookings.length - 1}
                    />
                  ))}
                </Stack>
              )}
            </SectionCard>

          </Stack>
        </Grid>
      </Grid>

      {/* Editorial section divider — "Calendar" zone */}
      <SectionDivider label="Calendar" />

      {/* ══ BOOKING DENSITY — 90-day forward heatmap ══════════════════
          GitHub-contributions grid styled in champagne tints. Each
          cell click could route to a filtered day view; today is
          ringed in gold so the rep always sees where they are in the
          calendar at a glance. */}
      <Paper elevation={0} sx={{
        mt: 3,
        p: { xs: 3, md: 4 },
        borderRadius: SURFACE.cardRadius,
        border: SURFACE.cardBorder,
        bgcolor: SURFACE.card,
        boxShadow: SURFACE.cardShadow,
      }}>
        <Box mb={2.5}>
          <Typography sx={{
            fontSize: 10, fontWeight: 600, letterSpacing: "0.22em",
            color: CHAMPAGNE_DEEP, textTransform: "uppercase",
          }}>
            {personalMode ? "My Schedule" : "Booking Density"}
          </Typography>
          <Typography sx={{
            fontFamily: FONT_SERIF,
            fontSize: 22, fontWeight: 400,
            color: INK, lineHeight: 1.1, mt: 0.5,
            letterSpacing: "-0.015em",
          }}>
            {personalMode ? "My next ninety days" : "The next ninety days"}
          </Typography>
          <Typography sx={{ fontSize: 11, color: "#64748b", mt: 0.6, fontStyle: "italic", fontFamily: FONT_SERIF }}>
            {personalMode
              ? "My confirmed events per day · champagne intensity scales with volume"
              : "Confirmed events per day · champagne intensity scales with volume"}
          </Typography>
        </Box>
        <BookingDensity
          byDate={bookingsByDate}
          days={90}
          onDateClick={(iso) => navigate(`/leads/inquiries?event_date=${iso}${personalMode ? "&view=my" : ""}`)}
        />
      </Paper>

      {/* ══ RECENT SMS — cross-lead provider feed ════════════════════
          Renders the latest SMS sends across every lead the operator
          can see. Status chips reflect Twilio's actual reply (sent /
          failed / delivered / queued) so the dashboard surfaces
          provider issues immediately — failed sends never get buried
          on a per-lead timeline. Empty state collapses gracefully. */}
      <Paper elevation={0} sx={{
        mt: 3,
        p: { xs: 3, md: 4 },
        borderRadius: SURFACE.cardRadius,
        border: SURFACE.cardBorder,
        bgcolor: SURFACE.card,
        boxShadow: SURFACE.cardShadow,
      }}>
        <Stack direction="row" alignItems="center" justifyContent="space-between" mb={2.5}>
          <Box>
            <Typography sx={{
              fontSize: 10, fontWeight: 600, letterSpacing: "0.22em",
              color: CHAMPAGNE_DEEP, textTransform: "uppercase",
            }}>
              Provider Activity
            </Typography>
            <Typography sx={{
              fontFamily: FONT_SERIF,
              fontSize: 22, fontWeight: 400,
              color: INK, lineHeight: 1.1, mt: 0.5,
              letterSpacing: "-0.015em",
            }}>
              Recent SMS
            </Typography>
            <Typography sx={{ fontSize: 11, color: "#64748b", mt: 0.6, fontStyle: "italic", fontFamily: FONT_SERIF }}>
              Last {recentSms.length || 0} outbound message{recentSms.length === 1 ? "" : "s"} · provider status reflected in real time
            </Typography>
          </Box>
          <SmsOutlinedIcon sx={{ fontSize: 28, color: CHAMPAGNE_DEEP }} />
        </Stack>

        {recentSms.length === 0 ? (
          <Box sx={{
            p: 3, borderRadius: 1.5, textAlign: "center",
            bgcolor: "#f8fafc", border: "1px dashed #e2e8f0",
          }}>
            <Typography sx={{ fontSize: 12, color: "#94a3b8" }}>
              No SMS activity yet.
            </Typography>
          </Box>
        ) : (
          <Stack gap={1}>
            {recentSms.map((sms: any) => {
              const isFailed   = sms.status === "failed" || sms.status === "undelivered";
              const isSent     = sms.status === "sent" || sms.status === "delivered";
              const isQueued   = sms.status === "queued" || sms.status === "disabled";
              const statusBg   = isFailed ? "#fee2e2" : isSent ? "#dcfce7" : "#fef3c7";
              const statusFg   = isFailed ? "#991b1b" : isSent ? "#166534" : "#92400e";
              const StatusIcon = isFailed ? ErrorOutlineIcon : isSent ? CheckCircleOutlineIcon : HourglassEmptyIcon;
              return (
                <Box
                  key={sms.id}
                  onClick={() => sms.lead_id && openLead(sms.lead_id)}
                  sx={{
                    p: 1.4, borderRadius: 1.5,
                    bgcolor: isFailed ? "#fef2f2" : "#fafbfc",
                    border: `1px solid ${isFailed ? "#fecaca" : HAIRLINE}`,
                    cursor: sms.lead_id ? "pointer" : "default",
                    transition: "all 0.15s",
                    "&:hover": sms.lead_id ? {
                      borderColor: CHAMPAGNE, bgcolor: "#fffdf6",
                    } : {},
                  }}
                >
                  <Stack direction="row" alignItems="center" gap={1} mb={0.5} flexWrap="wrap">
                    <Chip
                      size="small"
                      icon={<StatusIcon sx={{ fontSize: 11 }} />}
                      label={sms.status}
                      sx={{
                        height: 18, fontSize: 9.5, fontWeight: 700,
                        bgcolor: statusBg, color: statusFg,
                        textTransform: "uppercase", letterSpacing: 0.4,
                        "& .MuiChip-icon": { color: statusFg, ml: 0.5 },
                      }}
                    />
                    {sms.ai_generated && (
                      <Chip
                        size="small"
                        icon={<AutoAwesomeIcon sx={{ fontSize: 10 }} />}
                        label="AI"
                        sx={{
                          height: 18, fontSize: 9, fontWeight: 700,
                          bgcolor: "#f5f3ff", color: "#6d28d9",
                          "& .MuiChip-icon": { color: "#6d28d9", ml: 0.4 },
                        }}
                      />
                    )}
                    {sms.lead && (
                      <Typography sx={{ fontSize: 11.5, fontWeight: 700, color: INK }}>
                        {sms.lead.name}
                        {sms.lead.lead_number && (
                          <Typography component="span" sx={{ fontSize: 10, color: "#94a3b8", ml: 0.6, fontFamily: "'JetBrains Mono', monospace" }}>
                            {sms.lead.lead_number}
                          </Typography>
                        )}
                      </Typography>
                    )}
                    <Typography sx={{
                      fontSize: 10.5, color: "#64748b",
                      fontFamily: "'JetBrains Mono', monospace",
                    }}>
                      → {sms.phone}
                    </Typography>
                    <Box sx={{ flex: 1 }} />
                    <Typography sx={{ fontSize: 10, color: "#94a3b8", fontWeight: 600 }}>
                      {new Date(sms.created_at).toLocaleString("en-US", {
                        month: "short", day: "numeric",
                        hour: "numeric", minute: "2-digit",
                      })}
                    </Typography>
                  </Stack>
                  <Typography sx={{
                    fontSize: 12, color: "#475569", lineHeight: 1.4,
                    whiteSpace: "pre-wrap", wordBreak: "break-word",
                  }}>
                    {(sms.message || "").length > 180
                      ? `${sms.message.slice(0, 180)}…`
                      : sms.message}
                  </Typography>
                  {isFailed && sms.error_message && (
                    <Typography sx={{
                      fontSize: 10.5, color: "#b91c1c", mt: 0.5, fontStyle: "italic",
                    }}>
                      Provider: {sms.error_message}
                    </Typography>
                  )}
                </Box>
              );
            })}
          </Stack>
        )}
      </Paper>

      {/* ── 80px footer breathing room ─── luxury is whitespace ─── */}
      <Box sx={{ height: 80 }} />

      {/* ══ FLOATING QUICK-ACTION RAIL — desktop only ════════════════
          A vertical strip of four icon pills fixed to the right edge of
          the viewport. Always within thumb's reach; never in the way.
          Hidden on tablet/mobile (the touch surface area changes the
          ergonomics — it'd just be redundant with the main UI). */}
      <Box sx={{
        position: "fixed",
        right: 20,
        top: "50%",
        transform: "translateY(-50%)",
        display: { xs: "none", lg: "flex" },
        flexDirection: "column",
        gap: 1.2,
        zIndex: 1100,
      }}>
        {[
          { label: "New Lead",     icon: <PeopleIcon sx={{ fontSize: 18 }} />,         action: () => navigate("/leads/inquiries?new=1") },
          { label: "New Quote",    icon: <RequestQuoteOutlinedIcon sx={{ fontSize: 18 }} />, action: () => navigate("/leads/inquiries?status=ready_for_quote") },
          { label: "Log Activity", icon: <NotificationsActiveIcon sx={{ fontSize: 18 }} />,  action: () => navigate("/leads/inquiries") },
          { label: "Quick Search", icon: <HistoryIcon sx={{ fontSize: 18 }} />,        action: () => navigate("/leads/inquiries") },
        ].map((b) => (
          <Tooltip key={b.label} title={b.label} placement="left" arrow>
            <IconButton
              onClick={b.action}
              sx={{
                width: 42, height: 42,
                bgcolor: "#ffffff",
                color: INK,
                border: `1px solid ${HAIRLINE}`,
                boxShadow: "0 2px 6px rgba(10,14,26,0.06), 0 8px 24px rgba(10,14,26,0.06)",
                transition: "all 200ms cubic-bezier(0.16, 1, 0.3, 1)",
                "&:hover": {
                  bgcolor: CHAMPAGNE,
                  color: "#ffffff",
                  borderColor: CHAMPAGNE,
                  transform: "translateX(-2px)",
                  boxShadow: "0 4px 14px rgba(201,169,97,0.35), 0 12px 32px rgba(10,14,26,0.10)",
                },
              }}
            >
              {b.icon}
            </IconButton>
          </Tooltip>
        ))}
      </Box>

      {/* ── Follow-up Detail Modal — opened from Latest Lead Updates
           rows, Today's Follow-ups items, and My Desk tasks. Keeps the
           operator in dashboard context while exposing the same Quick
           Actions, follow-up controls, and AI suggestions available on
           the full lead detail page. */}
      <FollowUpDetailModal
        leadId={modalLeadId}
        onClose={() => setModalLeadId(null)}
        onChanged={() => { loadData(); loadLatestUpdates(); }}
      />
    </Box>
  );
};

export default DCCDashboard;

/* ═══════════════════════════════════════════════════════════════════════
   Sub-components
   ═══════════════════════════════════════════════════════════════════════ */

interface KpiDef {
  id: string;
  label: string;
  value: number | string;
  sub: string;
  icon: React.ReactNode;
  accent: string;
  /** % change vs last week (rounded). Positive = up, negative = down.
   *  `null` means we don't have a comparable signal and the delta line
   *  shows the static `sub` caption instead. */
  delta: number | null;
  onClick?: () => void;
}

/**
 * Single KPI cell inside the shared hairline-divider row. No card
 * borders / shadows of its own — those live on the enclosing Paper.
 * The numeral is the hero: oversized serif, light weight, tabular
 * digits. The label is tiny tracked-out sans below. A 4px corner
 * accent dot carries the colour signal.
 */
function KpiCard({ def, isFirstInRow }: { def: KpiDef; isFirstInRow?: boolean }) {
  // Only show a delta line when the parent supplied a real one (e.g.
  // weekDelta from this_week / last_week on the stats payload). No
  // synthesized 14-day series — sparkline removed.
  const hasDelta = def.delta !== null;
  const deltaPositive = (def.delta ?? 0) >= 0;
  return (
    <Box
      onClick={def.onClick}
      sx={{
        position: "relative",
        // Vertical hairline divider between cells — never on the
        // first cell in a row. Border-left only.
        borderLeft: isFirstInRow ? "none" : `1px solid ${HAIRLINE}`,
        px: 2.1,
        pt: 2.1,
        pb: 0,
        cursor: def.onClick ? "pointer" : "default",
        transition: "background 200ms ease-out",
        overflow: "hidden",
        "&:hover": def.onClick ? {
          bgcolor: "rgba(201,169,97,0.04)",
        } : {},
      }}
    >
      {/* 4px corner accent dot — the only colour signal on the tile */}
      <Box sx={{
        position: "absolute", top: 10, left: isFirstInRow ? 10 : 12,
        width: 5, height: 5, borderRadius: "50%",
        bgcolor: def.accent,
      }} />

      {/* Oversized serif numeral — the data is the hero */}
      <Typography sx={{
        fontFamily: FONT_SERIF,
        fontSize: { xs: 32, lg: 38 },
        fontWeight: 300,
        color: INK,
        lineHeight: 1,
        letterSpacing: "-0.02em",
        fontVariantNumeric: "tabular-nums",
      }}>
        {def.value}
      </Typography>

      {/* Tiny tracked-out sans label */}
      <Typography sx={{
        fontSize: 9.5,
        fontWeight: 600,
        color: "#64748b",
        letterSpacing: "0.16em",
        textTransform: "uppercase",
        mt: 1.1,
        whiteSpace: "nowrap",
        overflow: "hidden",
        textOverflow: "ellipsis",
      }}>
        {def.label}
      </Typography>

      {/* Delta line — % vs last period, derived from the sparkline.
          Sub-pixel sized to never compete with the numeral. */}
      <Stack direction="row" alignItems="center" gap={0.5} mt={0.6} sx={{ minHeight: 14 }}>
        {hasDelta ? (
          <>
            <Box
              component="span"
              sx={{
                fontSize: 10, fontWeight: 700,
                color: deltaPositive ? SAGE : WINE,
                fontVariantNumeric: "tabular-nums",
                letterSpacing: "0.02em",
              }}
            >
              {deltaPositive ? "↗" : "↘"} {Math.abs(def.delta ?? 0)}%
            </Box>
            <Box component="span" sx={{ fontSize: 10, color: "#94a3b8", letterSpacing: "0.05em" }}>
              vs last period
            </Box>
          </>
        ) : (
          <Box component="span" sx={{ fontSize: 10, color: "#94a3b8", letterSpacing: "0.05em" }}>
            {def.sub}
          </Box>
        )}
      </Stack>

      {/* Bottom breathing room — replaces the prior 32px sparkline so
          the tile keeps its vertical rhythm with the row. */}
      <Box sx={{ height: 16 }} />
    </Box>
  );
}

function SectionCard({
  title, subtitle, accentIcon, children, onSeeAll, elevated, muted,
}: {
  title: string;
  subtitle?: string;
  accentIcon?: React.ReactNode;
  children: React.ReactNode;
  onSeeAll?: () => void;
  /** Hero-level section. Warmer background, larger serif header, a
   *  champagne left-border accent, and a softly amplified shadow. */
  elevated?: boolean;
  /** Sidebar / secondary panel treatment. Drops shadow in favor of a
   *  hairline border, shrinks the header to 15px, tightens padding. */
  muted?: boolean;
}) {
  return (
    <Card elevation={0} sx={{
      borderRadius: SURFACE.cardRadius,
      border: muted ? `1px solid ${HAIRLINE}` : SURFACE.cardBorder,
      bgcolor: elevated ? "#FDFBF7" : SURFACE.card,
      boxShadow: muted
        ? "none"
        : (elevated
          ? "0 1px 2px rgba(10,14,26,0.04), 0 8px 24px rgba(168,137,62,0.06)"
          : SURFACE.cardShadow),
      borderLeft: elevated ? `3px solid ${CHAMPAGNE}` : undefined,
      overflow: "hidden",
    }}>
      {/* Editorial header — small editorial serif title with the
          accentIcon tucked alongside, microcopy underneath. The 8px
          base grid drives the paddings (24/16).
          elevated: header inflates (24px serif) + bigger paddings.
          muted:    header tightens (15px serif) + smaller paddings. */}
      <Stack
        direction="row" alignItems="center" gap={1.2}
        sx={{
          px: elevated ? 3.5 : muted ? 2.4 : 3,
          py: elevated ? 2.6 : muted ? 1.6 : 2,
          borderBottom: SURFACE.divider,
        }}
      >
        {accentIcon && (
          <Box sx={{ display: "inline-flex", color: CHAMPAGNE_DEEP, opacity: 0.85 }}>
            {accentIcon}
          </Box>
        )}
        <Box flex={1} minWidth={0}>
          <Typography sx={{
            fontFamily: FONT_SERIF,
            fontSize: elevated ? 24 : muted ? 15 : 17,
            fontWeight: elevated ? 400 : 500,
            color: INK,
            letterSpacing: "-0.015em",
            lineHeight: 1.15,
          }}>
            {title}
          </Typography>
          {subtitle && (
            <Typography sx={{
              fontSize: muted ? 10.5 : 11,
              color: "#64748b",
              mt: elevated ? 0.5 : 0.3,
              letterSpacing: "0.01em",
              fontStyle: elevated ? "italic" : "normal",
              fontFamily: elevated ? FONT_SERIF : undefined,
            }} noWrap>
              {subtitle}
            </Typography>
          )}
        </Box>
        {onSeeAll && (
          <Button
            size="small"
            onClick={onSeeAll}
            endIcon={<ChevronRightIcon sx={{ fontSize: "14px !important", ml: -0.5 }} />}
            sx={{
              textTransform: "uppercase", fontSize: 10.5, fontWeight: 700,
              color: "#64748b", minWidth: 0, letterSpacing: "0.08em",
              transition: "color 200ms ease-out",
              "&:hover": { color: CHAMPAGNE_DEEP, bgcolor: "transparent" },
            }}
          >
            View all
          </Button>
        )}
      </Stack>
      <Box>{children}</Box>
    </Card>
  );
}

function EmptyState({
  icon, text, action,
}: {
  icon: React.ReactNode;
  text: string;
  /** Optional inline CTA — a small gold-tinted button rendered below the
   *  copy. When the action is omitted the empty state is purely
   *  decorative; when present the empty state becomes a quiet
   *  invitation rather than a dead end. */
  action?: { label: string; onClick: () => void };
}) {
  return (
    <Stack alignItems="center" justifyContent="center" gap={1.5} sx={{ py: 5, px: 3 }}>
      <Box sx={{ color: CHAMPAGNE_DEEP, opacity: 0.6 }}>{icon}</Box>
      <Typography sx={{
        fontFamily: FONT_SERIF,
        fontStyle: "italic",
        fontSize: 13.5,
        fontWeight: 400,
        color: "#64748b",
        textAlign: "center",
        maxWidth: 300,
        lineHeight: 1.55,
      }}>
        {text}
      </Typography>
      {action && (
        <Button
          size="small"
          onClick={action.onClick}
          sx={{
            mt: 0.4,
            textTransform: "none",
            fontSize: 11.5,
            fontWeight: 600,
            letterSpacing: "0.05em",
            color: INK,
            bgcolor: `${CHAMPAGNE}15`,
            border: `1px solid ${CHAMPAGNE}50`,
            borderRadius: "999px",
            px: 2,
            py: 0.5,
            transition: "all 200ms cubic-bezier(0.16, 1, 0.3, 1)",
            "&:hover": {
              bgcolor: `${CHAMPAGNE}28`,
              borderColor: CHAMPAGNE_DEEP,
              color: CHAMPAGNE_DEEP,
            },
          }}
        >
          {action.label} →
        </Button>
      )}
    </Stack>
  );
}

function StatusPill({ status }: { status: string }) {
  const cfg = STATUS[status];
  if (!cfg) return null;
  // Soft-pastel pill on near-ink text — replaces the bright
  // saturated chip with a confident, restrained luxury chip.
  return (
    <Box sx={{
      display: "inline-flex", alignItems: "center",
      fontSize: 9.5, fontWeight: 600,
      color: INK,
      bgcolor: `${cfg.color}10`,
      border: "none",
      borderRadius: "999px",
      px: 0.9, py: 0.15,
      letterSpacing: "0.08em",
      textTransform: "uppercase",
    }}>
      <Box component="span" sx={{
        width: 5, height: 5, borderRadius: "50%",
        bgcolor: cfg.color, mr: 0.6,
      }} />
      {cfg.label}
    </Box>
  );
}

function leadInitial(name: string | null | undefined): string {
  if (!name) return "?";
  const parts = name.trim().split(/\s+/);
  return ((parts[0]?.[0] || "") + (parts[1]?.[0] || "")).toUpperCase() || "?";
}

function avatarColor(seed: string): string {
  const palette = ["#3b82f6", "#8b5cf6", "#f59e0b", "#16a34a", "#dc2626", "#0ea5e9", "#a855f7", "#14b8a6"];
  let hash = 0;
  for (let i = 0; i < seed.length; i++) hash = (hash * 31 + seed.charCodeAt(i)) >>> 0;
  return palette[hash % palette.length];
}

/* ── Urgent lane classifier — single source of truth for the chip
   strip + per-row priority bar. Order matters: "overdue" trumps
   "hot" trumps "unassigned". */
function urgentReason(lead: any): { key: "overdue" | "hot" | "unassigned" | "attention"; label: string; color: string; bgPale: string } {
  const isOverdue    = !!(lead.follow_up_required && lead.follow_up_at && lead.follow_up_at < todayStr);
  const isHot        = lead.ai_score === "hot";
  const isUnassigned = !lead.assigned_to;
  // Disciplined 4-color palette — burgundy / champagne / graphite /
  // sage — replaces the previous bright red/orange/slate/blue chips.
  // Pale grounds carry the wash; bold variant carries the chip text.
  if (isOverdue)     return { key: "overdue",    label: "Past Due",            color: WINE,           bgPale: WINE_PALE };
  if (isHot)         return { key: "hot",        label: "High Intent",         color: CHAMPAGNE_DEEP, bgPale: CHAMPAGNE_PALE };
  if (isUnassigned)  return { key: "unassigned", label: "Awaiting Assignment", color: "#475569",      bgPale: "rgba(10,14,26,0.04)" };
  return                    { key: "attention",  label: "Attention",           color: SAGE,           bgPale: SAGE_PALE };
}

function applyUrgentFilter(items: any[], filter: "all" | "overdue" | "hot" | "unassigned" | "today"): any[] {
  if (filter === "all")        return items;
  if (filter === "today")      return items.filter((l) => (l.follow_up_at || "").startsWith(todayStr));
  return items.filter((l) => urgentReason(l).key === filter);
}

function UrgentFilterStrip({
  items, filter, onChange,
}: {
  items: any[];
  filter: "all" | "overdue" | "hot" | "unassigned" | "today";
  onChange: (f: "all" | "overdue" | "hot" | "unassigned" | "today") => void;
}) {
  // Pre-compute counts per lane so each chip shows the size of the
  // segment without re-iterating during render.
  const counts = {
    all:        items.length,
    overdue:    items.filter((l) => urgentReason(l).key === "overdue").length,
    hot:        items.filter((l) => urgentReason(l).key === "hot").length,
    unassigned: items.filter((l) => urgentReason(l).key === "unassigned").length,
    today:      items.filter((l) => (l.follow_up_at || "").startsWith(todayStr)).length,
  };
  const chips: { key: typeof filter; label: string }[] = [
    { key: "all",        label: "All" },
    { key: "overdue",    label: "Past Due" },
    { key: "hot",        label: "High Intent" },
    { key: "unassigned", label: "Awaiting" },
    { key: "today",      label: "Today" },
  ];

  return (
    <Stack direction="row" gap={0.6} flexWrap="wrap"
      sx={{ px: 3, pt: 1.6, pb: 1.6, borderBottom: SURFACE.divider, bgcolor: "#fcfbf8" }}
    >
      {chips.map((c) => {
        const active = filter === c.key;
        const n = counts[c.key];
        return (
          <Box
            key={c.key}
            role="button"
            onClick={() => onChange(c.key)}
            sx={{
              cursor: "pointer", userSelect: "none",
              display: "inline-flex", alignItems: "center", gap: 0.5,
              px: 1.2, py: 0.4,
              borderRadius: "999px",
              fontSize: 10.5, fontWeight: 600,
              letterSpacing: "0.05em",
              color: active ? CHAMPAGNE_DEEP : "#64748b",
              bgcolor: active ? "rgba(201,169,97,0.10)" : "transparent",
              border: `1px solid ${active ? `${CHAMPAGNE}55` : HAIRLINE}`,
              transition: "all 200ms ease-out",
              "&:hover": active ? {} : { color: INK, borderColor: HAIRLINE_LIGHT, bgcolor: "rgba(10,14,26,0.025)" },
            }}
          >
            {c.label}
            <Box component="span" sx={{
              fontSize: 9.5, fontWeight: 700,
              color: active ? CHAMPAGNE_DEEP : "#94a3b8",
              fontVariantNumeric: "tabular-nums",
              ml: 0.2,
            }}>
              {n}
            </Box>
          </Box>
        );
      })}
    </Stack>
  );
}

function UrgentLeadRow({ lead, onClick, isLast }: { lead: any; onClick: () => void; isLast: boolean }) {
  const reason = urgentReason(lead);
  const assignee =
    lead.assignee?.display_name ||
    [lead.assignee?.first_name, lead.assignee?.last_name].filter(Boolean).join(" ") ||
    lead.assignee?.username ||
    null;

  // Time-since-created ghost line — "created 3h ago". Lives below the
  // event date so the rep sees both "when's the event" + "how stale is
  // the lead" without opening anything.
  const createdAgo = lead.created_at ? fmtRelativeTime(lead.created_at) : null;

  return (
    <Stack
      direction="row" alignItems="stretch" gap={0}
      onClick={onClick}
      sx={{
        position: "relative",
        borderBottom: isLast ? "none" : SURFACE.divider,
        cursor: "pointer",
        transition: "background 250ms cubic-bezier(0.16, 1, 0.3, 1)",
        // Cream wash on hover. Also gently lifts a soft shadow + slides
        // the chevron 4px right. Class hooks live below.
        "& .urgent-chevron": {
          transition: "transform 250ms cubic-bezier(0.16, 1, 0.3, 1), color 250ms",
        },
        "&:hover": {
          bgcolor: "#FDFAF4",
          boxShadow: "inset 4px 0 0 -1px rgba(201,169,97,0.18)",
        },
        "&:hover .urgent-chevron": {
          transform: "translateX(4px)",
          color: CHAMPAGNE_DEEP,
        },
      }}
    >
      {/* Thin vertical priority bar at the left edge — instant
          scanability without needing to read the chip label. */}
      <Box sx={{ width: 3, flexShrink: 0, bgcolor: reason.color, opacity: 0.85 }} />

      <Stack direction="row" alignItems="center" gap={1.6} sx={{ flex: 1, px: 2.4, py: 1.6, minWidth: 0 }}>
        {/* Gradient serif avatar — initials in editorial serif, sitting
            on a soft champagne-tinted disc that signals "premium guest"
            not "generic user". */}
        <Box sx={{
          width: 36, height: 36, borderRadius: "999px", flexShrink: 0,
          display: "flex", alignItems: "center", justifyContent: "center",
          background: `linear-gradient(135deg, ${CHAMPAGNE}22 0%, ${IVORY_DEEP} 100%)`,
          border: `1px solid ${CHAMPAGNE}33`,
          color: CHAMPAGNE_DEEP,
          fontFamily: FONT_SERIF,
          fontSize: 14, fontWeight: 500, lineHeight: 1,
        }}>
          {leadInitial(lead.name)}
        </Box>

        <Box flex={1} minWidth={0}>
          <Stack direction="row" alignItems="center" gap={0.8} flexWrap="wrap">
            <Typography sx={{
              fontSize: 13.5, fontWeight: 600, color: INK,
              textTransform: "capitalize", letterSpacing: "-0.005em",
            }} noWrap>
              {lead.name?.toLowerCase()}
            </Typography>
            {/* Reason chip — soft pastel ground, dark text */}
            <Box sx={{
              display: "inline-flex", alignItems: "center",
              fontSize: 9.5, fontWeight: 600, letterSpacing: "0.08em",
              bgcolor: reason.bgPale, color: reason.color,
              borderRadius: "999px",
              px: 0.9, py: 0.15,
              textTransform: "uppercase",
            }}>
              {reason.label}
            </Box>
            <StatusPill status={lead.status} />
          </Stack>
          <Stack direction="row" alignItems="baseline" gap={0.6} mt={0.4} flexWrap="wrap">
            {lead.event_type && (
              <Box sx={{
                fontFamily: FONT_SERIF, fontStyle: "italic",
                fontSize: 11.5, color: "#64748b", textTransform: "capitalize",
              }}>
                {lead.event_type.replace(/_/g, " ")}
              </Box>
            )}
            {lead.event_date && (
              <Typography sx={{ fontSize: 10.5, color: "#94a3b8" }}>
                · {fmtEventDate(lead.event_date)}
              </Typography>
            )}
            {createdAgo && (
              <Typography sx={{
                fontSize: 10, color: "#cbd5e1", fontStyle: "italic",
                fontFamily: FONT_SERIF, ml: 0.4,
              }}>
                · created {createdAgo}
              </Typography>
            )}
          </Stack>
        </Box>

        <Box textAlign="right" sx={{ flexShrink: 0 }}>
          <Typography sx={{
            fontFamily: FONT_MONO, fontSize: 10.5, color: "#475569",
            fontVariantNumeric: "tabular-nums", letterSpacing: "0.02em",
          }}>
            {lead.follow_up_at ? fmtRelativeTime(lead.follow_up_at) : "—"}
          </Typography>
          <Typography sx={{ fontSize: 10, color: "#94a3b8", mt: 0.2 }} noWrap>
            {assignee || COPY.unassigned}
          </Typography>
        </Box>
        <ChevronRightIcon
          className="urgent-chevron"
          sx={{ fontSize: 16, color: "#cbd5e1", flexShrink: 0 }}
        />
      </Stack>
    </Stack>
  );
}

/* ─────────────────────────────────────────────────────────────────────
   FollowUpTaskRow — task-sourced version of the row used by "Today's
   Follow-ups". Reads from a lead_tasks Task row (eager-loaded with
   `lead` and `assignee` server-side), so it can surface per-task
   signals the lead-flag query couldn't: attempts count, last-attempt
   outcome, the task's actual assignee (which may differ from the
   lead's). Click handler still opens the lead drawer for the operator
   to act on. */
function FollowUpTaskRow({ task, onClick, isLast }: { task: any; onClick: () => void; isLast: boolean }) {
  const dueIso = task.due_at as string | null;
  const overdueByTime = !!(dueIso && new Date(dueIso).getTime() < Date.now());
  const lead = task.lead;
  const a = task.assignee;
  const assignee =
    a?.display_name ||
    [a?.first_name, a?.last_name].filter(Boolean).join(" ").trim() ||
    a?.username ||
    null;
  const attempts = task.follow_up_attempts ?? 0;
  return (
    <Stack
      direction="row" alignItems="center" gap={1.2}
      onClick={onClick}
      sx={{
        px: 2, py: 1.1,
        borderBottom: isLast ? "none" : SURFACE.divider,
        cursor: "pointer",
        "&:hover": { bgcolor: "#fafbfc" },
      }}
    >
      <Box sx={{
        width: 50, height: 38, borderRadius: "8px",
        bgcolor: overdueByTime ? "#fef2f2" : "#eff6ff",
        color: overdueByTime ? "#dc2626" : "#3b82f6",
        display: "flex", alignItems: "center", justifyContent: "center",
        flexShrink: 0,
        fontVariantNumeric: "tabular-nums",
      }}>
        <Typography sx={{ fontSize: 11, fontWeight: 800, lineHeight: 1 }}>
          {fmtClockTime(dueIso)}
        </Typography>
      </Box>
      <Box flex={1} minWidth={0}>
        <Stack direction="row" alignItems="center" gap={0.6} flexWrap="wrap">
          <Typography sx={{ fontSize: 12.5, fontWeight: 700, color: NAVY, textTransform: "capitalize" }} noWrap>
            {(lead?.name ?? "").toLowerCase() || "—"}
          </Typography>
          {overdueByTime && (
            <Box sx={{ fontSize: 9, fontWeight: 800, color: "#dc2626", border: "1px solid #fecaca", borderRadius: "5px", px: 0.45, py: 0.05, letterSpacing: 0.4 }}>
              OVERDUE
            </Box>
          )}
          {attempts > 0 && (
            <Box sx={{ fontSize: 9, fontWeight: 800, color: "#4338ca", border: "1px solid #c7d2fe", bgcolor: "#eef2ff", borderRadius: "5px", px: 0.45, py: 0.05, letterSpacing: 0.4 }}>
              {attempts} ATTEMPT{attempts === 1 ? "" : "S"}
            </Box>
          )}
        </Stack>
        <Typography sx={{ fontSize: 11, color: "#64748b", mt: 0.1, textTransform: "capitalize" }} noWrap>
          {lead?.event_type?.replace(/_/g, " ") || "—"}
          {" · "}
          {assignee || <span style={{ color: "#94a3b8" }}>Unassigned</span>}
        </Typography>
      </Box>
      <ChevronRightIcon sx={{ fontSize: 16, color: "#cbd5e1", flexShrink: 0 }} />
    </Stack>
  );
}

/* ─────────────────────────────────────────────────────────────────────────
   StackedPipelineBar — horizontal 8px bar segmented by lead stage
   ─────────────────────────────────────────────────────────────────────────
   Renders one continuous bar split into proportional champagne / bronze /
   graphite segments with hairline white dividers. Single source of truth
   for the "pipeline at a glance" visual — used both in the personal
   pipeline panel and in the standalone Pipeline section.
   ─────────────────────────────────────────────────────────────────────── */
function StackedPipelineBar({
  byStatus, total, stages,
}: {
  byStatus: Record<string, number>;
  total: number;
  stages: { key: string; label: string; color: string }[];
}) {
  if (total <= 0) {
    return (
      <Box sx={{
        height: 8, borderRadius: "999px",
        bgcolor: HAIRLINE_LIGHT,
      }} />
    );
  }
  return (
    <Box sx={{
      display: "flex", height: 8, borderRadius: "999px",
      overflow: "hidden",
      bgcolor: HAIRLINE_LIGHT,
      boxShadow: "inset 0 0 0 1px rgba(10,14,26,0.03)",
    }}>
      {stages.map((s, i) => {
        const count = byStatus[s.key] ?? 0;
        if (count === 0) return null;
        const pct = (count / total) * 100;
        return (
          <Tooltip key={s.key} title={`${s.label} · ${count}`} arrow>
            <Box
              sx={{
                width: `${pct}%`,
                bgcolor: s.color,
                borderRight: i < stages.length - 1 ? "1.5px solid #fff" : "none",
                transition: "filter 200ms cubic-bezier(0.16, 1, 0.3, 1)",
                cursor: "default",
                "&:hover": { filter: "brightness(1.08)" },
              }}
            />
          </Tooltip>
        );
      })}
    </Box>
  );
}

/* ─────────────────────────────────────────────────────────────────────────
   Latest Lead Updates — lead-centric operational feed
   ONE row per lead, enriched with status / assignee / follow-up timing /
   AI signal + a compact preview of the lead's 3 most recent activity
   events. Server-side intelligent sort puts overdue follow-ups and
   awaiting-assignment leads at the top, then recent customer replies,
   then AI-hot leads, then newest.
   ─────────────────────────────────────────────────────────────────────── */

type LatestUpdatesFilterKey = "all" | "mine" | "new" | "follow_up_due" | "high_interest" | "awaiting_assignment" | "vip";

const LATEST_UPDATES_FILTERS: { value: LatestUpdatesFilterKey; label: string }[] = [
  { value: "all",                 label: "All" },
  { value: "mine",                label: "Mine" },
  { value: "new",                 label: "New" },
  { value: "follow_up_due",       label: "Follow-up Due" },
  { value: "high_interest",       label: "High Interest" },
  { value: "awaiting_assignment", label: "Awaiting Assignment" },
  { value: "vip",                 label: "VIP" },
];

/** Status palette for the centre badge on each lead row. Mirrors the
 *  global STATUS map but keeps the operational labels short. */
const STATUS_LABEL: Record<string, string> = {
  new:               "New Inquiry",
  contacted:         "Contacted",
  negotiation:       "Negotiation",
  ready_for_quote:   "Quote Sent",
  ready_for_booking: "Booking Ready",
  confirmed:         "Confirmed",
  lost:              "Lost",
};

/** Source pill text — short uppercase code that fits inside an 11px chip
 *  without overflowing the centre column on tablet widths. */
function sourceLabel(src?: string | null): string {
  if (!src) return "—";
  const s = String(src).toLowerCase();
  if (s === "website")  return "WEB";
  if (s === "walk_in" || s === "walkin") return "WALK-IN";
  return s.toUpperCase();
}

/** Maps each priority bucket to its left-rail accent. */
const PRIORITY_RAIL: Record<string, string> = {
  red:    "#dc2626",
  orange: "#f97316",
  blue:   "#3b82f6",
  green:  "#16a34a",
};

/** Maps a single preview activity row to a tiny visual + sentence so
 *  the inline timeline strip reads naturally ("AI converted inquiry
 *  into lead", "Williams contacted customer", …). */
function previewLine(a: any): { icon: React.ReactNode; color: string; text: string } {
  const meta = a.meta ?? {};
  const sub = String(meta.type ?? "").toLowerCase();
  const actor = a.actor_name || "Someone";
  switch (a.type) {
    case "created": {
      const src = String(meta.source ?? "").toLowerCase();
      if (src === "email") return { icon: <AutoAwesomeIcon sx={{ fontSize: 11 }} />, color: "#16a34a", text: "AI converted inquiry into lead" };
      return { icon: <FiberManualRecordIcon sx={{ fontSize: 9 }} />, color: "#3b82f6", text: "Lead created" };
    }
    case "contact_logged":
      if (sub === "call" || sub === "phone")        return { icon: <CallIcon sx={{ fontSize: 11 }} />,        color: "#16a34a", text: `${actor} called the customer` };
      if (sub === "email")                          return { icon: <MailOutlineIcon sx={{ fontSize: 11 }} />, color: "#8b5cf6", text: `${actor} emailed the customer` };
      if (sub === "meeting" || sub === "in_person") return { icon: <HandshakeIcon sx={{ fontSize: 11 }} />,   color: "#8b5cf6", text: `${actor} met with the customer` };
      if (sub === "voicemail")                      return { icon: <CallIcon sx={{ fontSize: 11 }} />,        color: "#f59e0b", text: `${actor} left a voicemail` };
      return                                              { icon: <CallIcon sx={{ fontSize: 11 }} />,        color: "#16a34a", text: `${actor} contacted the customer` };
    case "note_added":     return { icon: <EditNoteIcon sx={{ fontSize: 11 }} />,        color: "#8b5cf6", text: `${actor} added a note` };
    case "status_changed": {
      const from = a.changes?.status?.from?.label ?? a.changes?.status?.from?.value ?? "—";
      const to   = a.changes?.status?.to?.label   ?? a.changes?.status?.to?.value   ?? "—";
      return { icon: <SwapHorizIcon sx={{ fontSize: 11 }} />, color: "#3b82f6", text: `${from} → ${to}` };
    }
    case "assigned":
      return { icon: <PersonAddAltIcon sx={{ fontSize: 11 }} />, color: "#3b82f6", text: `Assigned to ${a.changes?.assigned_to?.to?.label ?? actor}` };
    case "task_created":     return { icon: <AccessTimeIcon sx={{ fontSize: 11 }} />,        color: "#f97316", text: "Follow-up scheduled" };
    case "task_completed":   return { icon: <CheckCircleOutlineIcon sx={{ fontSize: 11 }} />, color: "#16a34a", text: "Follow-up completed" };
    case "task_rescheduled": return { icon: <AccessTimeIcon sx={{ fontSize: 11 }} />,        color: "#f59e0b", text: "Follow-up rescheduled" };
    case "task_cancelled":   return { icon: <WarningIcon sx={{ fontSize: 11 }} />,           color: "#dc2626", text: "Follow-up cancelled" };
    case "email_sent":       return { icon: <MailOutlineIcon sx={{ fontSize: 11 }} />,       color: "#8b5cf6", text: `${actor} replied by email` };
    case "email_received":   return { icon: <MailOutlineIcon sx={{ fontSize: 11 }} />,       color: "#8b5cf6", text: "Customer replied by email" };
    default:                 return { icon: <HistoryIcon sx={{ fontSize: 11 }} />,           color: "#64748b", text: a.description || a.type.replace(/_/g, " ") };
  }
}

function fmtRelativeShort(iso?: string | null): string {
  if (!iso) return "—";
  const t = new Date(iso).getTime();
  if (!isFinite(t)) return "—";
  const diff = Date.now() - t;
  if (diff < 60_000)         return "just now";
  const mins = Math.floor(diff / 60_000);
  if (mins < 60)             return `${mins} min${mins === 1 ? "" : "s"} ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24)              return `${hrs} hour${hrs === 1 ? "" : "s"} ago`;
  const days = Math.floor(hrs / 24);
  if (days < 7)              return `${days} day${days === 1 ? "" : "s"} ago`;
  return new Date(iso).toLocaleDateString("en-US", { month: "short", day: "numeric" });
}

/** Tiny KPI chip rendered in the metrics strip above the filter tabs.
 *  Compact, scannable — value first, label after, color accent only on
 *  the value so the strip stays elegant rather than dashboard-busy. */
function MetricChip({ label, value, accent }: { label: string; value: number; accent: string }) {
  return (
    <Box sx={{
      display: "flex", alignItems: "baseline", gap: 0.5,
      px: 1.1, py: 0.35,
      bgcolor: "#fff",
      border: `1px solid ${HAIRLINE}`,
      borderRadius: "8px",
      whiteSpace: "nowrap",
    }}>
      <Typography sx={{
        fontFamily: FONT_MONO, fontSize: 12, fontWeight: 700,
        color: accent, fontVariantNumeric: "tabular-nums",
        lineHeight: 1,
      }}>
        {value}
      </Typography>
      <Typography sx={{
        fontSize: 9.5, fontWeight: 600, color: "#64748b",
        letterSpacing: "0.04em",
      }}>
        {label}
      </Typography>
    </Box>
  );
}

/** Empty-state copy varies by tab so the section says something useful
 *  instead of always falling back to a generic "nothing here". */
function emptyCopy(filter: LatestUpdatesFilterKey): string {
  switch (filter) {
    case "follow_up_due":       return "All follow-ups completed.";
    case "awaiting_assignment": return "No leads awaiting assignment.";
    case "high_interest":       return "No high-interest leads right now.";
    case "vip":                 return "No VIP leads in flight.";
    case "new":                 return "No new inquiries waiting.";
    case "mine":                return "Nothing assigned to you right now.";
    default:                    return "No recent lead activity.";
  }
}

function LatestLeadUpdatesCard({
  metrics, leads, loading, filter, onFilterChange, onNavigate, onOpenLead,
}: {
  metrics: { active_conversations: number; follow_ups_overdue: number; awaiting_assignment: number; replies_today: number };
  leads: any[];
  loading: boolean;
  filter: LatestUpdatesFilterKey;
  onFilterChange: (f: LatestUpdatesFilterKey) => void;
  onNavigate: (path: string) => void;
  /** Opens the Follow-up Detail Modal. Clicking a lead row (or any of
   *  its activity preview lines) hands the lead id up to the dashboard
   *  shell which controls the modal mount. */
  onOpenLead: (id: number) => void;
}) {
  return (
    <Card elevation={0} sx={{
      borderRadius: SURFACE.cardRadius,
      border: SURFACE.cardBorder,
      bgcolor: SURFACE.card,
      boxShadow: SURFACE.cardShadow,
      overflow: "hidden",
    }}>
      {/* ── Header + LIVE + metrics strip ───────────────────────── */}
      <Stack direction="row" alignItems="center" gap={1.2} flexWrap="wrap" useFlexGap
             sx={{ px: 2.4, py: 1.4, borderBottom: SURFACE.divider }}>
        <HistoryIcon sx={{ fontSize: 16, color: NAVY }} />
        <Box minWidth={0} sx={{ mr: 0.8 }}>
          <Stack direction="row" alignItems="center" gap={0.8}>
            <Typography sx={{
              fontFamily: FONT_SERIF, fontSize: 16, fontWeight: 400,
              color: INK, letterSpacing: "-0.015em", lineHeight: 1.15,
            }}>
              Latest Lead Updates
            </Typography>
            <Stack direction="row" alignItems="center" gap={0.4} sx={{
              bgcolor: "rgba(22,163,74,0.10)",
              border: "1px solid rgba(22,163,74,0.28)",
              borderRadius: "999px",
              px: 0.7, py: 0.15,
            }}>
              <Box sx={{
                width: 6, height: 6, borderRadius: "50%",
                bgcolor: "#16a34a",
                animation: "live-pulse 1.8s ease-out infinite",
                "@keyframes live-pulse": {
                  "0%":   { boxShadow: "0 0 0 0 rgba(22,163,74,0.7)" },
                  "70%":  { boxShadow: "0 0 0 6px rgba(22,163,74,0)" },
                  "100%": { boxShadow: "0 0 0 0 rgba(22,163,74,0)" },
                },
              }} />
              <Typography sx={{
                fontFamily: FONT_MONO, fontSize: 8.5, fontWeight: 600,
                color: "#16a34a", letterSpacing: "0.16em",
              }}>
                LIVE
              </Typography>
            </Stack>
          </Stack>
          <Typography sx={{ fontSize: 10.5, color: "#94a3b8", mt: 0.1 }} noWrap>
            Auto-refreshing as the team works
          </Typography>
        </Box>

        <Box flex={1} />

        {/* Operational metrics — sits right of the title on desktop,
            wraps below on tablet. Color accents pull the eye toward the
            number, not the label. */}
        <Stack direction="row" gap={0.6} flexWrap="wrap" useFlexGap>
          <MetricChip label="active"        value={metrics.active_conversations} accent={INK} />
          <MetricChip label="overdue"       value={metrics.follow_ups_overdue}   accent="#dc2626" />
          <MetricChip label="unassigned"    value={metrics.awaiting_assignment}  accent="#f97316" />
          <MetricChip label="replies today" value={metrics.replies_today}        accent="#16a34a" />
        </Stack>
      </Stack>

      {/* ── Tabs — state-based filters answer real questions ──── */}
      <Box sx={{
        display: "flex", flexWrap: "wrap", gap: 0.4,
        px: 2, py: 1, borderBottom: SURFACE.divider, bgcolor: "#fafbfc",
      }}>
        {LATEST_UPDATES_FILTERS.map((f) => {
          const active = filter === f.value;
          return (
            <Box
              key={f.value}
              role="button"
              onClick={() => onFilterChange(f.value)}
              sx={{
                cursor: "pointer", userSelect: "none",
                px: 1, py: 0.3, borderRadius: "6px",
                fontSize: 10.5, fontWeight: active ? 700 : 600,
                color: active ? NAVY : "#94a3b8",
                bgcolor: active ? "#fff" : "transparent",
                border: active ? `1px solid ${NAVY}` : "1px solid transparent",
                transition: "all 0.15s",
                "&:hover": active ? {} : { color: NAVY },
              }}
            >
              {f.label}
            </Box>
          );
        })}
      </Box>

      {/* ── Rows ──────────────────────────────────────────────────── */}
      {loading ? (
        <Box sx={{ py: 4, display: "flex", justifyContent: "center" }}>
          <Box sx={{
            width: 22, height: 22, borderRadius: "50%",
            border: "2px solid #eef0f4", borderTopColor: GOLD,
            animation: "spin 0.9s linear infinite",
            "@keyframes spin": { "0%": { transform: "rotate(0deg)" }, "100%": { transform: "rotate(360deg)" } },
          }} />
        </Box>
      ) : leads.length === 0 ? (
        <EmptyState
          icon={<TrailIcon size={36} />}
          text={emptyCopy(filter)}
          action={filter === "all" ? { label: "View All Leads", onClick: () => onNavigate("/leads/inquiries") } : undefined}
        />
      ) : (
        <Stack>
          {leads.map((lead, i) => (
            <LatestLeadRow
              key={lead.thread_id}
              lead={lead}
              isLast={i === leads.length - 1}
              onNavigate={onNavigate}
              onOpenLead={onOpenLead}
            />
          ))}
        </Stack>
      )}
    </Card>
  );
}

/** Single row in the Latest Lead Updates feed.
 *  Three-zone compact layout: identity → operational context →
 *  signal/timing. Left rail = priority color. Hover shows a row of
 *  quick actions. Activity preview strip lives directly under the
 *  identity column. */
function LatestLeadRow({
  lead, isLast, onNavigate, onOpenLead,
}: {
  lead: any;
  isLast: boolean;
  onNavigate: (path: string) => void;
  onOpenLead: (id: number) => void;
}) {
  const rail = PRIORITY_RAIL[lead.priority_color] ?? PRIORITY_RAIL.green;
  const statusLabel = STATUS_LABEL[lead.status] ?? lead.status;
  const statusColor = STATUS[lead.status]?.color ?? "#64748b";

  // Operational badges — quiet by default, vivid only when truly
  // operationally meaningful. The right-edge column hosts them so the
  // eye finds them in the same place on every row.
  const badges: { label: string; color: string; bg: string }[] = [];
  if (lead.follow_up_overdue)   badges.push({ label: "Follow-up Due",        color: "#dc2626", bg: "#fef2f2" });
  else if (lead.follow_up_due_today) badges.push({ label: "Due Today",       color: "#f97316", bg: "#fff7ed" });
  if (lead.newly_received)      badges.push({ label: "New Inquiry",          color: "#3b82f6", bg: "#eff6ff" });
  if (lead.awaiting_assignment) badges.push({ label: "Awaiting Assignment",  color: "#f97316", bg: "#fff7ed" });
  if (lead.high_interest)       badges.push({ label: "High Interest",        color: "#dc2626", bg: "#fef2f2" });
  if (lead.is_vip)              badges.push({ label: "VIP",                  color: CHAMPAGNE_DEEP, bg: CHAMPAGNE_PALE });
  if (lead.booking_ready)       badges.push({ label: "Booking Ready",        color: "#16a34a", bg: "#dcfce7" });

  const eventDate = lead.event_date
    ? new Date(lead.event_date).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })
    : null;

  return (
    <Box
      onClick={() => onOpenLead(lead.thread_id)}
      sx={{
        position: "relative",
        px: 2.2, py: 1.1, pl: 2.5,
        borderBottom: isLast ? "none" : SURFACE.divider,
        cursor: "pointer",
        transition: "background 200ms cubic-bezier(0.16, 1, 0.3, 1)",
        "&:hover": { bgcolor: "#FDFAF4" },
        "&:hover .lead-quick-actions": { opacity: 1, pointerEvents: "auto" },
      }}
    >
      {/* Priority left rail — single color stripe drives at-a-glance
          triage: red = overdue, orange = today, blue = new, green = healthy. */}
      <Box sx={{
        position: "absolute", left: 0, top: 6, bottom: 6,
        width: 3, borderRadius: "0 2px 2px 0",
        bgcolor: rail,
      }} />

      <Stack direction="row" alignItems="flex-start" gap={1.6}>
        {/* ZONE 1 — LEAD IDENTITY ──────────────────────────────────── */}
        <Box sx={{ minWidth: 0, flex: "1 1 38%" }}>
          <Stack direction="row" alignItems="center" gap={0.8}>
            <UserAvatar
              user={{ profile_image: null, name: lead.name }}
              size={28}
              tooltip={false}
            />
            <Box minWidth={0}>
              <Stack direction="row" alignItems="center" gap={0.6} flexWrap="wrap" useFlexGap>
                <Typography sx={{
                  fontSize: 13, fontWeight: 700, color: NAVY,
                  textTransform: "capitalize", lineHeight: 1.2,
                }} noWrap>
                  {(lead.name || "Unknown").toLowerCase()}
                </Typography>
                {lead.lead_number && (
                  <Typography sx={{
                    fontFamily: FONT_MONO, fontSize: 9.5, fontWeight: 600,
                    color: "#94a3b8", letterSpacing: "0.02em",
                  }}>
                    {lead.lead_number}
                  </Typography>
                )}
              </Stack>
              {/* Event meta strip — event type, date, guest count */}
              <Stack direction="row" alignItems="center" gap={0.7} mt={0.3} flexWrap="wrap" useFlexGap>
                {lead.event_type && (
                  <Box sx={{
                    px: 0.7, py: 0.1, borderRadius: "4px",
                    bgcolor: CHAMPAGNE_PALE, color: CHAMPAGNE_DEEP,
                    fontSize: 9.5, fontWeight: 700,
                    textTransform: "capitalize", letterSpacing: "0.02em",
                  }}>
                    {String(lead.event_type).replace(/_/g, " ")}
                  </Box>
                )}
                {eventDate && (
                  <Stack direction="row" alignItems="center" gap={0.2}>
                    <CalendarMonthOutlinedIcon sx={{ fontSize: 11, color: "#94a3b8" }} />
                    <Typography sx={{ fontSize: 10, color: "#64748b", fontVariantNumeric: "tabular-nums" }}>
                      {eventDate}
                    </Typography>
                  </Stack>
                )}
                {lead.guest_count != null && (
                  <Stack direction="row" alignItems="center" gap={0.2}>
                    <GroupOutlinedIcon sx={{ fontSize: 11, color: "#94a3b8" }} />
                    <Typography sx={{ fontSize: 10, color: "#64748b", fontVariantNumeric: "tabular-nums" }}>
                      {lead.guest_count}
                    </Typography>
                  </Stack>
                )}
              </Stack>
            </Box>
          </Stack>

          {/* AI insight one-liner — only when the heuristic surfaced
              something operationally useful. */}
          {lead.ai_insight && (
            <Stack direction="row" alignItems="center" gap={0.4} mt={0.6}>
              <AutoAwesomeIcon sx={{ fontSize: 11, color: CHAMPAGNE_DEEP }} />
              <Typography sx={{ fontSize: 10.5, color: "#475569", fontStyle: "italic" }} noWrap>
                {lead.ai_insight}
              </Typography>
            </Stack>
          )}

          {/* Activity preview strip — last 3 events, tiny dot list */}
          {Array.isArray(lead.activities) && lead.activities.length > 0 && (
            <Stack mt={0.7} gap={0.25}>
              {lead.activities.slice(0, 3).map((a: any) => {
                const pl = previewLine(a);
                return (
                  <Stack key={a.id} direction="row" alignItems="center" gap={0.5}>
                    <Box sx={{ color: pl.color, display: "flex", alignItems: "center" }}>{pl.icon}</Box>
                    <Typography sx={{ fontSize: 10.5, color: "#475569", lineHeight: 1.35 }} noWrap>
                      {pl.text}
                    </Typography>
                    <Box flex={1} />
                    <Typography sx={{ fontSize: 9.5, color: "#94a3b8", fontVariantNumeric: "tabular-nums" }}>
                      {fmtRelativeShort(a.created_at)}
                    </Typography>
                  </Stack>
                );
              })}
            </Stack>
          )}
        </Box>

        {/* ZONE 2 — OPERATIONAL CONTEXT ─────────────────────────────── */}
        <Box sx={{ minWidth: 0, flex: "0 1 26%", display: { xs: "none", md: "block" } }}>
          {/* Status pill */}
          <Box sx={{
            display: "inline-flex", alignItems: "center", gap: 0.4,
            px: 0.8, py: 0.2, borderRadius: "999px",
            bgcolor: `${statusColor}14`, color: statusColor,
            fontSize: 10, fontWeight: 700,
            letterSpacing: "0.04em", textTransform: "uppercase",
          }}>
            <Box sx={{ width: 5, height: 5, borderRadius: "50%", bgcolor: statusColor }} />
            {statusLabel}
          </Box>

          {/* Assignee row */}
          <Stack direction="row" alignItems="center" gap={0.55} mt={0.6}>
            {lead.assignee ? (
              <>
                <UserAvatar
                  user={{ profile_image: lead.assignee.image, name: lead.assignee.name }}
                  size={16}
                  tooltip={false}
                />
                <Typography sx={{ fontSize: 10.5, fontWeight: 700, color: "#475569" }} noWrap>
                  {lead.assignee.name}
                </Typography>
              </>
            ) : (
              <>
                <PersonOffIcon sx={{ fontSize: 12, color: "#f97316" }} />
                <Typography sx={{ fontSize: 10.5, fontWeight: 700, color: "#f97316" }}>
                  Unassigned
                </Typography>
              </>
            )}
          </Stack>

          {/* Source pill */}
          <Box sx={{
            display: "inline-flex", alignItems: "center", mt: 0.6,
            px: 0.7, py: 0.15, borderRadius: "4px",
            bgcolor: "#f1f5f9", color: "#475569",
            fontFamily: FONT_MONO, fontSize: 9, fontWeight: 700,
            letterSpacing: "0.05em",
          }}>
            {sourceLabel(lead.source)}
          </Box>
        </Box>

        {/* ZONE 3 — SIGNAL / TIMING + QUICK ACTIONS ───────────────── */}
        <Box sx={{
          minWidth: 0, flex: "0 1 26%",
          display: "flex", flexDirection: "column", alignItems: "flex-end",
          gap: 0.4,
        }}>
          <Typography sx={{
            fontSize: 10, color: "#94a3b8",
            fontVariantNumeric: "tabular-nums",
          }}>
            {fmtRelativeShort(lead.last_activity_at)}
          </Typography>

          {/* Operational badges — limit to 2 visible to avoid pile-up;
              the rest are still implied by the priority rail color. */}
          {badges.length > 0 && (
            <Stack direction="row" gap={0.35} flexWrap="wrap" justifyContent="flex-end" useFlexGap>
              {badges.slice(0, 2).map((b) => (
                <Box key={b.label} sx={{
                  px: 0.7, py: 0.15, borderRadius: "4px",
                  bgcolor: b.bg, color: b.color,
                  fontSize: 9, fontWeight: 700,
                  letterSpacing: "0.04em", textTransform: "uppercase",
                  whiteSpace: "nowrap",
                }}>
                  {b.label}
                </Box>
              ))}
            </Stack>
          )}

          {/* AI confidence pill — only when ai_score is meaningful. */}
          {lead.ai_score && lead.ai_score !== "cold" && (
            <Stack direction="row" alignItems="center" gap={0.3} sx={{
              px: 0.7, py: 0.15, borderRadius: "999px",
              bgcolor: lead.ai_score === "hot" ? "rgba(220,38,38,0.08)" : "rgba(245,158,11,0.10)",
              border: lead.ai_score === "hot" ? "1px solid rgba(220,38,38,0.2)" : "1px solid rgba(245,158,11,0.22)",
            }}>
              <LocalFireDepartmentIcon sx={{
                fontSize: 10,
                color: lead.ai_score === "hot" ? "#dc2626" : "#f59e0b",
              }} />
              <Typography sx={{
                fontFamily: FONT_MONO, fontSize: 8.5, fontWeight: 700,
                color: lead.ai_score === "hot" ? "#dc2626" : "#f59e0b",
                letterSpacing: "0.06em", textTransform: "uppercase",
              }}>
                AI · {lead.ai_score}
              </Typography>
            </Stack>
          )}

          {/* Hover quick actions — desktop only. Stops propagation so
              clicking a button doesn't fall through to the row's
              navigate-to-lead handler. */}
          <Stack
            className="lead-quick-actions"
            direction="row" gap={0.2}
            sx={{
              opacity: 0, pointerEvents: "none",
              transition: "opacity 150ms ease",
              display: { xs: "none", md: "flex" },
              mt: 0.3,
            }}
          >
            <QuickActionIconButton
              tip="Open Lead"
              icon={<OpenInNewIcon sx={{ fontSize: 13 }} />}
              onClick={(e) => { e.stopPropagation(); onNavigate(`/leads/${lead.thread_id}`); }}
            />
            <QuickActionIconButton
              tip="Add Note"
              icon={<EditNoteIcon sx={{ fontSize: 13 }} />}
              onClick={(e) => { e.stopPropagation(); onNavigate(`/leads/${lead.thread_id}#note`); }}
            />
            <QuickActionIconButton
              tip="Mark Contacted"
              icon={<DoneAllIcon sx={{ fontSize: 13 }} />}
              onClick={(e) => { e.stopPropagation(); onNavigate(`/leads/${lead.thread_id}#contact`); }}
            />
            <QuickActionIconButton
              tip="Assign Staff"
              icon={<PersonAddAltIcon sx={{ fontSize: 13 }} />}
              onClick={(e) => { e.stopPropagation(); onNavigate(`/leads/${lead.thread_id}#assign`); }}
            />
            <QuickActionIconButton
              tip="Schedule Follow-up"
              icon={<AccessTimeIcon sx={{ fontSize: 13 }} />}
              onClick={(e) => { e.stopPropagation(); onNavigate(`/leads/${lead.thread_id}#follow-up`); }}
            />
          </Stack>
        </Box>

        <ChevronRightIcon sx={{ fontSize: 15, color: "#cbd5e1", flexShrink: 0, mt: 0.3 }} />
      </Stack>
    </Box>
  );
}

/** Tiny tooltip-wrapped icon button used by the row's hover action
 *  cluster. Tap target is generous (24×24) but the icon itself stays
 *  13px so a five-button row still fits the dashboard column. */
function QuickActionIconButton({
  tip, icon, onClick,
}: {
  tip: string;
  icon: React.ReactNode;
  onClick: (e: React.MouseEvent) => void;
}) {
  return (
    <Tooltip title={tip} arrow placement="top">
      <IconButton
        size="small"
        onClick={onClick}
        sx={{
          width: 24, height: 24,
          color: "#64748b",
          bgcolor: "rgba(255,255,255,0.85)",
          border: `1px solid ${HAIRLINE}`,
          "&:hover": { color: CHAMPAGNE_DEEP, bgcolor: "#fff" },
        }}
      >
        {icon}
      </IconButton>
    </Tooltip>
  );
}

function ActivityRow({ lead, onClick, isLast }: { lead: any; onClick: () => void; isLast: boolean }) {
  const cfg = STATUS[lead.status];
  return (
    <Stack
      direction="row" alignItems="center" gap={1.2}
      onClick={onClick}
      sx={{
        px: 2, py: 0.9,
        borderBottom: isLast ? "none" : SURFACE.divider,
        cursor: "pointer",
        "&:hover": { bgcolor: "#fafbfc" },
      }}
    >
      <Box sx={{
        width: 24, height: 24, borderRadius: "50%",
        bgcolor: `${cfg?.color || "#94a3b8"}14`,
        display: "flex", alignItems: "center", justifyContent: "center",
        flexShrink: 0,
      }}>
        <Box sx={{ width: 6, height: 6, borderRadius: "50%", bgcolor: cfg?.color || "#94a3b8" }} />
      </Box>
      <Box flex={1} minWidth={0}>
        <Typography sx={{ fontSize: 12, color: "#475569" }} noWrap>
          <Box component="span" sx={{ fontWeight: 700, color: NAVY, textTransform: "capitalize" }}>
            {lead.name?.toLowerCase()}
          </Box>
          {" · "}
          <Box component="span" sx={{ color: cfg?.color || "#94a3b8", fontWeight: 600 }}>
            {cfg?.label || lead.status}
          </Box>
          {lead.event_type && (
            <Box component="span" sx={{ color: "#94a3b8", textTransform: "capitalize" }}>
              {" · "}{lead.event_type.replace(/_/g, " ")}
            </Box>
          )}
        </Typography>
      </Box>
      <Typography sx={{ fontSize: 10.5, color: "#94a3b8", fontVariantNumeric: "tabular-nums", flexShrink: 0 }}>
        {fmtRelativeTime(lead.created_at)}
      </Typography>
    </Stack>
  );
}

function TaskRow({ task, onClick, isLast }: { task: any; onClick: () => void; isLast: boolean }) {
  const due       = new Date(task.due_at);
  const timeStr   = isFinite(due.getTime()) ? due.toLocaleTimeString([], { hour: "numeric", minute: "2-digit", hour12: true }) : "—";
  const isOverdue = task._section === "overdue";
  return (
    <Stack
      direction="row" alignItems="center" gap={1.2}
      onClick={onClick}
      sx={{
        px: 2, py: 1,
        borderBottom: isLast ? "none" : SURFACE.divider,
        cursor: "pointer",
        "&:hover": { bgcolor: "#fafbfc" },
      }}
    >
      <Box sx={{
        width: 8, height: 8, borderRadius: "50%",
        bgcolor: isOverdue ? "#dc2626" : "#3b82f6",
        flexShrink: 0,
      }} />
      <Box flex={1} minWidth={0}>
        <Typography sx={{ fontSize: 12, fontWeight: 600, color: isOverdue ? "#dc2626" : NAVY }} noWrap>
          {task.title}
        </Typography>
        <Stack direction="row" alignItems="center" gap={0.6} mt={0.1}>
          <Typography sx={{ fontSize: 10.5, color: "#94a3b8", fontVariantNumeric: "tabular-nums" }}>
            {timeStr}
          </Typography>
          {task.lead?.name && (
            <Typography sx={{ fontSize: 10.5, color: "#94a3b8" }} noWrap>
              · {task.lead.name}
            </Typography>
          )}
        </Stack>
      </Box>
      {task.lead_id && (
        <OpenInNewIcon sx={{ fontSize: 13, color: "#cbd5e1", flexShrink: 0 }} />
      )}
    </Stack>
  );
}

function TeamRow({ user, count, maxCount, onClick, isLast }: { user: any; count: number; maxCount: number; onClick: () => void; isLast: boolean }) {
  const name =
    user.display_name ||
    [user.first_name, user.last_name].filter(Boolean).join(" ") ||
    user.username;
  const pct = maxCount > 0 ? (count / maxCount) * 100 : 0;

  // Workload tier — derived from the rep's count relative to the
  // busiest rep on the floor. Reads at a glance whether the rep is
  // light, balanced, or overloaded so the manager can rebalance
  // without doing the math.
  let tier: { label: string; color: string; bg: string };
  if (count === 0) {
    tier = { label: "Idle",     color: "#94a3b8", bg: "rgba(10,14,26,0.04)" };
  } else if (pct >= 75) {
    tier = { label: "Overload", color: WINE,           bg: WINE_PALE };
  } else if (pct >= 40) {
    tier = { label: "Active",   color: CHAMPAGNE_DEEP, bg: CHAMPAGNE_PALE };
  } else {
    tier = { label: "Light",    color: SAGE,           bg: SAGE_PALE };
  }

  return (
    <Stack
      direction="row" alignItems="center" gap={1.1}
      onClick={onClick}
      sx={{
        px: 2, py: 0.9,
        borderBottom: isLast ? "none" : SURFACE.divider,
        cursor: "pointer",
        transition: "background 200ms cubic-bezier(0.16, 1, 0.3, 1)",
        "&:hover": { bgcolor: "#FDFAF4" },
      }}
    >
      {/* Real user avatar with online indicator — a small sage dot on
          the bottom-right edge signals the rep is "on the floor"
          (count > 0 = at least one assigned lead). */}
      <Box sx={{ position: "relative", flexShrink: 0 }}>
        <UserAvatar user={user} size={26} />
        {count > 0 && (
          <Box sx={{
            position: "absolute", bottom: -1, right: -1,
            width: 8, height: 8, borderRadius: "50%",
            bgcolor: SAGE, border: "2px solid #fff",
          }} />
        )}
      </Box>
      <Box flex={1} minWidth={0}>
        <Stack direction="row" justifyContent="space-between" alignItems="center" mb={0.3}>
          <Stack direction="row" alignItems="center" gap={0.7} minWidth={0}>
            <Typography sx={{ fontSize: 12, fontWeight: 600, color: INK, letterSpacing: "-0.005em" }} noWrap>
              {name}
            </Typography>
            <Box sx={{
              display: "inline-flex", alignItems: "center",
              fontSize: 8.5, fontWeight: 700, letterSpacing: "0.08em",
              color: tier.color, bgcolor: tier.bg,
              borderRadius: "999px",
              px: 0.6, py: 0.05,
              textTransform: "uppercase",
              flexShrink: 0,
            }}>
              {tier.label}
            </Box>
          </Stack>
          <Typography sx={{
            fontFamily: FONT_SERIF, fontSize: 13.5, fontWeight: 400,
            color: count > 0 ? INK : "#94a3b8",
            fontVariantNumeric: "tabular-nums", lineHeight: 1,
            letterSpacing: "-0.015em",
          }}>
            {count}
          </Typography>
        </Stack>
        <LinearProgress
          variant="determinate" value={pct}
          sx={{
            height: 3, borderRadius: 2,
            bgcolor: "rgba(10,14,26,0.04)",
            "& .MuiLinearProgress-bar": {
              bgcolor: count === 0 ? "#cbd5e1" : tier.color,
              borderRadius: 2,
            },
          }}
        />
      </Box>
    </Stack>
  );
}

/* ──────────────────────────────────────────────────────────────────────
   My Lead Status Summary
   Personalized replacement for the Team Performance widget shown to
   non-admin / non-sales-manager roles. Surfaces only the rep's own
   leads — never company-wide stats, never other staff names.

   Layout (top → bottom):
     1. 4 compact summary tiles (Total Assigned · Contacted Today
        · Conversion % · Avg Response)
     2. 6 status rows (New / Contacted / Negotiation / Quote Sent /
        Booking Ready / Confirmed) — each with dot, label, count,
        mini progress bar
     3. Quick actions (View My Leads · Add Follow-up)

   The card matches Team Performance's height / spacing so the grid
   never shifts when one role swaps for another.
   ──────────────────────────────────────────────────────────────────────── */
const MY_PIPELINE: { key: string; label: string; color: string }[] = [
  { key: "new",               label: "New Inquiry",  color: STATUS.new.color },
  { key: "contacted",         label: "Contacted",    color: STATUS.contacted.color },
  { key: "negotiation",       label: "Negotiation",  color: STATUS.negotiation.color },
  { key: "ready_for_quote",   label: "Quote Sent",   color: STATUS.ready_for_quote.color },
  { key: "ready_for_booking", label: "Booking Ready",color: STATUS.ready_for_booking.color },
  { key: "confirmed",         label: "Confirmed",    color: STATUS.confirmed.color },
];

function MyLeadStatusSummary({
  stats, userId, onNavigate,
}: {
  stats: any;
  userId: number;
  onNavigate: (path: string) => void;
}) {
  // Pre-data state — no more pulsing skeleton bars. The dashboard now
  // shows a calm serif placeholder while the assigned-leads payload
  // is in flight. Same visual rhythm as the populated card so the
  // page doesn't reflow on arrival.
  if (!stats) {
    return (
      <SectionCard
        title="Pipeline"
        subtitle="Your assigned leads"
        accentIcon={<AssignmentIndOutlinedIcon sx={{ fontSize: 16 }} />}
      >
        <Box sx={{ px: 3, py: 4, textAlign: "center" }}>
          <Typography sx={{
            fontFamily: FONT_SERIF, fontStyle: "italic",
            fontSize: 13, color: "#94a3b8",
          }}>
            Tuning the dashboard…
          </Typography>
        </Box>
      </SectionCard>
    );
  }

  const total: number       = stats.total_assigned ?? 0;
  const byStatus            = stats.by_status ?? {};
  const contactedToday      = stats.contacted_today ?? 0;
  const followUpToday       = stats.follow_up_today ?? 0;
  const overdue             = stats.overdue ?? 0;
  const conversion: number  = stats.conversion_rate ?? 0;
  const avgRespMin: number  = stats.avg_response_minutes ?? 0;

  // Empty state — proper illustrated state instead of the cold
  // "No leads assigned yet" + grey button.
  if (total === 0) {
    return (
      <SectionCard
        title="Pipeline"
        subtitle="Your assigned leads"
        accentIcon={<AssignmentIndOutlinedIcon sx={{ fontSize: 16 }} />}
      >
        <EmptyState
          icon={<QuillIcon size={36} />}
          text="Your pipeline is empty — claim a lead from the unassigned pool to begin."
          action={{ label: "Browse Unassigned", onClick: () => onNavigate("/leads/inquiries?unassigned=1") }}
        />
      </SectionCard>
    );
  }

  // Drive the mini progress bars off the largest single-status count so
  // every bar fills relative to the rep's hottest stage.
  const maxStatusCount = Math.max(1, ...MY_PIPELINE.map((s) => byStatus[s.key] ?? 0));

  // Compact "Xh Ym" / "Xm" response-time display.
  const respLabel = avgRespMin <= 0
    ? "—"
    : avgRespMin < 60
      ? `${avgRespMin}m`
      : `${Math.floor(avgRespMin / 60)}h ${avgRespMin % 60}m`;

  const overdueOrFollowUpRoute = overdue > 0
    ? `/leads/inquiries?assigned_to=${userId}&follow_up=1`
    : `/leads/inquiries?assigned_to=${userId}&follow_up_today=1`;

  return (
    <SectionCard
      title="Pipeline"
      subtitle={`${total} assigned · your personal pipeline`}
      accentIcon={<AssignmentIndOutlinedIcon sx={{ fontSize: 16 }} />}
      onSeeAll={() => onNavigate(`/leads/inquiries?assigned_to=${userId}`)}
    >
      {/* ── Pipeline Donut — replaces the old skeleton bars.
          Six segments in disciplined champagne/bronze/sage palette,
          stagger-animated on mount, hover-dim siblings. Sits in the
          header zone where the rep first lands. */}
      <Box sx={{ px: 3, pt: 2.4, pb: 2.4, borderBottom: SURFACE.divider }}>
        <PipelineDonut
          segments={MY_PIPELINE.map((s) => ({
            key: s.key,
            label: s.label,
            count: byStatus[s.key] ?? 0,
            color: s.color,
          }))}
        />
      </Box>

      {/* ── Top summary metrics — 4 compact tiles ────────────────── */}
      <Box sx={{
        display: "grid",
        gridTemplateColumns: { xs: "repeat(2, 1fr)", sm: "repeat(4, 1fr)" },
        gap: 0.8,
        px: 3, py: 1.6,
        borderBottom: SURFACE.divider,
      }}>
        <SummaryTile
          label="Assigned"
          value={total}
          accent={INK}
          onClick={() => onNavigate(`/leads/inquiries?assigned_to=${userId}`)}
        />
        <SummaryTile
          label="Today"
          sub="Contacted"
          value={contactedToday}
          accent={SAGE}
          onClick={() => onNavigate(`/leads/inquiries?assigned_to=${userId}`)}
        />
        <SummaryTile
          label="Conv %"
          value={`${conversion}%`}
          accent={CHAMPAGNE_DEEP}
          onClick={() => onNavigate(`/leads/inquiries?assigned_to=${userId}&status=confirmed`)}
        />
        <SummaryTile
          label="Avg Resp"
          value={respLabel}
          accent={WINE}
        />
      </Box>

      {/* ── 6 status rows with mini progress bars ────────────────── */}
      <Box sx={{ px: 2, py: 1.6 }}>
        <Stack spacing={1.2}>
          {MY_PIPELINE.map((s) => {
            const count = byStatus[s.key] ?? 0;
            const pct   = (count / maxStatusCount) * 100;
            return (
              <Box
                key={s.key}
                onClick={() => onNavigate(`/leads/inquiries?assigned_to=${userId}&status=${s.key}`)}
                sx={{ cursor: "pointer", transition: "opacity 0.12s", "&:hover": { opacity: 0.85 } }}
              >
                <Stack direction="row" alignItems="center" justifyContent="space-between" mb={0.4}>
                  <Stack direction="row" alignItems="center" gap={0.7}>
                    <Box sx={{ width: 8, height: 8, borderRadius: "50%", bgcolor: s.color, flexShrink: 0 }} />
                    <Typography sx={{ fontSize: 12, fontWeight: 600, color: NAVY }}>
                      {s.label}
                    </Typography>
                  </Stack>
                  <Typography sx={{
                    fontSize: 12, fontWeight: 800,
                    color: count > 0 ? s.color : "#cbd5e1",
                    fontVariantNumeric: "tabular-nums",
                  }}>
                    {count}
                  </Typography>
                </Stack>
                <LinearProgress
                  variant="determinate" value={pct}
                  sx={{
                    height: 4, borderRadius: 2,
                    bgcolor: "#f1f5f9",
                    "& .MuiLinearProgress-bar": { bgcolor: s.color, borderRadius: 2 },
                  }}
                />
              </Box>
            );
          })}
        </Stack>
      </Box>

      {/* ── Follow-up signal row (overdue / today) ───────────────────
          Tiny inline bar surfacing the operational state of my desk
          right above the action buttons. Click sends me straight to
          the filtered list — same behavior whether overdue or today. */}
      {(overdue > 0 || followUpToday > 0) && (
        <Stack
          direction="row" alignItems="center" gap={0.8}
          onClick={() => onNavigate(overdueOrFollowUpRoute)}
          sx={{
            mx: 2, mb: 1,
            px: 1.2, py: 0.8, borderRadius: "8px",
            bgcolor: overdue > 0 ? "#fef2f2" : "#eff6ff",
            border: `1px solid ${overdue > 0 ? "#fecaca" : "#bfdbfe"}`,
            cursor: "pointer",
            "&:hover": { borderColor: overdue > 0 ? "#dc2626" : "#3b82f6" },
          }}
        >
          {overdue > 0 ? (
            <WarningIcon sx={{ fontSize: 14, color: "#dc2626" }} />
          ) : (
            <AccessTimeIcon sx={{ fontSize: 14, color: "#3b82f6" }} />
          )}
          <Typography sx={{ fontSize: 11.5, fontWeight: 700, color: overdue > 0 ? "#dc2626" : "#3b82f6" }}>
            {overdue > 0
              ? `${overdue} overdue · ${followUpToday} due today`
              : `${followUpToday} follow-up${followUpToday === 1 ? "" : "s"} due today`}
          </Typography>
          <Box flex={1} />
          <ChevronRightIcon sx={{ fontSize: 14, color: overdue > 0 ? "#dc2626" : "#3b82f6" }} />
        </Stack>
      )}

      {/* ── Quick actions ─────────────────────────────────────────── */}
      <Stack direction="row" gap={1} sx={{ px: 2, pb: 1.6, pt: overdue === 0 && followUpToday === 0 ? 0.5 : 0 }}>
        <Button
          size="small"
          startIcon={<OpenInNewIcon sx={{ fontSize: "14px !important" }} />}
          onClick={() => onNavigate(`/leads/inquiries?assigned_to=${userId}`)}
          sx={{
            flex: 1, textTransform: "none", fontSize: 11.5, fontWeight: 700,
            color: NAVY, borderRadius: "8px",
            border: "1px solid #e5e7eb",
            "&:hover": { borderColor: NAVY, bgcolor: "#f8fafc" },
          }}
        >
          View My Leads
        </Button>
        <Button
          size="small"
          startIcon={<AddCircleOutlineIcon sx={{ fontSize: "14px !important" }} />}
          onClick={() => onNavigate("/tasks")}
          sx={{
            flex: 1, textTransform: "none", fontSize: 11.5, fontWeight: 700,
            color: "#fff", borderRadius: "8px",
            bgcolor: NAVY,
            "&:hover": { bgcolor: NAVY_LIGHT },
          }}
        >
          Add Follow-up
        </Button>
      </Stack>
    </SectionCard>
  );
}

function SummaryTile({
  label, sub, value, accent, onClick,
}: {
  label: string;
  sub?: string;
  value: number | string;
  accent: string;
  onClick?: () => void;
}) {
  return (
    <Box
      onClick={onClick}
      sx={{
        textAlign: "center", py: 0.9, borderRadius: "8px",
        bgcolor: "#fafbfc",
        border: "1px solid #eef0f4",
        cursor: onClick ? "pointer" : "default",
        transition: "all 0.15s",
        "&:hover": onClick ? { borderColor: accent, bgcolor: `${accent}08` } : {},
      }}
    >
      <Typography sx={{
        fontSize: 16, fontWeight: 800, color: accent, lineHeight: 1,
        letterSpacing: "-0.3px", fontVariantNumeric: "tabular-nums",
      }}>
        {value}
      </Typography>
      <Typography sx={{
        fontSize: 9, fontWeight: 700, color: "#94a3b8",
        letterSpacing: 0.4, textTransform: "uppercase", mt: 0.4,
      }}>
        {sub ? `${sub} ${label}` : label}
      </Typography>
    </Box>
  );
}

function BookingRow({ lead, onClick, isLast }: { lead: any; onClick: () => void; isLast: boolean }) {
  const value = Number(lead.budget_value ?? 0);
  return (
    <Stack
      direction="row" alignItems="center" gap={1.2}
      onClick={onClick}
      sx={{
        px: 2, py: 1.1,
        borderBottom: isLast ? "none" : SURFACE.divider,
        cursor: "pointer",
        "&:hover": { bgcolor: "#fafbfc" },
      }}
    >
      <Box sx={{
        width: 36, height: 36, borderRadius: "8px",
        bgcolor: "#f0fdf4", color: "#16a34a",
        display: "flex", alignItems: "center", justifyContent: "center",
        flexShrink: 0,
      }}>
        <EventAvailableIcon sx={{ fontSize: 16 }} />
      </Box>
      <Box flex={1} minWidth={0}>
        <Typography sx={{ fontSize: 12.5, fontWeight: 700, color: NAVY, textTransform: "capitalize" }} noWrap>
          {lead.name?.toLowerCase()}
        </Typography>
        <Typography sx={{ fontSize: 10.5, color: "#64748b", mt: 0.1, textTransform: "capitalize" }} noWrap>
          {lead.event_type?.replace(/_/g, " ") || "Event"}
          {lead.event_date && ` · ${fmtEventDate(lead.event_date)}`}
          {lead.guest_count != null && ` · ${lead.guest_count} guests`}
        </Typography>
      </Box>
      <Typography sx={{ fontSize: 12, fontWeight: 800, color: "#16a34a", fontVariantNumeric: "tabular-nums", flexShrink: 0 }}>
        {formatCurrency(value)}
      </Typography>
    </Stack>
  );
}
